var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
/* eslint-disable @typescript-eslint/no-empty-function */
import { useHistory, useParams } from 'react-router-dom';
import { useApolloClient, useLazyQuery } from '@apollo/client';
import { useAlert } from 'components/alert/AlertStack';
import { createContext, useContext, useEffect, useQuery, useState, useRef } from 'libs/react';
import { DealConfigurator } from './components/DealConfigurator';
import { DealSidebar } from './components/DealSidebar';
import { Content } from 'components/Content';
import { Header } from 'components/Header';
import { Screen } from 'components/Screen';
import { Sidebar } from 'components/Sidebar';
import { UpdateDealMutation } from 'graphql/Deal/mutations/UpdateDealMutation';
import { DealQuery } from 'graphql/Deal/queries/DealQuery';
import { React } from 'libs/react';
import { Text } from 'components/Text';
import { ConfigurationFieldsQuery } from 'graphql/ConfigurationField/queries/ConfigurationFieldsQuery';
import { Button } from 'components/Button';
import { DealConfiguratorModalPublish } from './components/modal/DealConfiguratorModalPublish';
import { useAccount } from 'components/account/AccountProvider';
import { DealStickyMenu } from './components/DealStickyMenu';
import { useModal } from 'components/modal/ModalStack';
import { getRelations } from 'helpers/Deal';
import { applyPercentPaymentAmounts, isPendingUpsellPayment, paymentScheduleBalance } from 'helpers/DealPaymentSchedule';
import { toMoney } from 'components/Money';
import { getPricePerUnitFromMarkup, getPricePerUnitFromMargin, getPriceFromItem, calculatePriceCurrency, getDiscountRep } from 'helpers/Pricing';
import { DealItemPicker } from './components/picker/DealItemPicker';
import { DealItemPickerOptionAlert } from './components/picker/DealItemPickerOptionAlert';
import { SaleItemQuery } from 'graphql/Deal/queries/dealItemPicker/SaleItemQuery';
import { parsePlannedDateToRange, resolvePlannedDateForUpdate } from 'helpers/DealQuantity';
import { getConfigurationFieldValue } from 'helpers/ConfigurationField';
import { v4 as uuidv4 } from 'uuid';
import { Spinner } from 'components/Spinner';
import { FieldGroupsQuery } from 'graphql/FieldGroup/queries/FieldGroupsQuery';
import { useUser } from 'components/user/UserProvider';
import { Confirm } from 'components/confirm/Confirm';
import { DealApproveModal } from './modal/DealApproveModal';
import { DealMewsAvailabilityBlockModal } from './modal/DealMewsAvailabilityBlockModal';
import { AccountAssetsQuery } from 'graphql/Account/queries/AccountAssetsQuery';
import { appendDealFreezeDebug, flushDealFreezeDebug, isDealFreezeDebugEnabled, startDealFreezeHeartbeat } from './dealConfigureFreezeDebug';
import { PriceListPricesQuery } from 'graphql/PriceList/queries/PriceListsQuery';
import { defaultDealPriceList, findPriceListPrice } from 'helpers/priceList';
import { copyDealItems, getInactiveDealItems } from 'helpers/dealItemCopy';
import { DealReplaceItemsModal } from './components/modal/DealReplaceItemsModal';
import './DealConfigureScreen.scss';
/**
 * @component DealConfigureScreen
 * @since 1.0.0
 */
export function DealConfigureScreen(props) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w, _x, _y, _z, _0, _1, _2, _3;
    const { id } = useParams();
    const apollo = useApolloClient();
    const alert = useAlert();
    const account = useAccount();
    const user = useUser();
    const history = useHistory();
    const modal = useModal();
    const [data, ready, error] = useQuery(DealQuery, {
        variables: {
            input: {
                id
            }
        }
    });
    const [configurationFields] = useQuery(ConfigurationFieldsQuery, {
        variables: {
            input: {
                category: 'DEAL'
            }
        }
    });
    const [fieldGroups] = useQuery(FieldGroupsQuery, {
        variables: {
            input: {
                query: [
                    {
                        field: 'category',
                        operator: 'in',
                        value: ['DEAL_ITEM', 'DEAL_ITEM_CONFIGURATION']
                    }
                ]
            }
        },
        fetchPolicy: 'network-only'
    });
    const canManageAssets = user.can('asset.manage');
    const canCheckExternalAvailability = user.can('calendar.externalAvailability.check');
    const canLoadAccountAssets = canManageAssets || canCheckExternalAvailability;
    const [accountAssetsData] = useQuery(AccountAssetsQuery, {
        variables: { input: {} },
        skip: !canLoadAccountAssets,
        fetchPolicy: canLoadAccountAssets ? 'cache-first' : 'cache-only'
    });
    // Same query and variables as the item picker: shared through the cache
    const [priceListsData] = useQuery(PriceListPricesQuery, {
        variables: { input: { active: true } },
        skip: !user.can('priceList.manage') && !user.can('deal.manage')
    });
    const [dealItemFields, setDealItemFields] = useState([]);
    const [dealItemConfigurationFields, setDealItemConfigurationFields] = useState([]);
    useEffect(() => {
        var _a, _b, _c, _d;
        appendDealFreezeDebug('effect.fieldGroups.sync.begin');
        setDealItemFields((_b = (_a = fieldGroups === null || fieldGroups === void 0 ? void 0 : fieldGroups.fieldGroups.filter((fieldGroup) => fieldGroup.category === 'DEAL_ITEM')[0]) === null || _a === void 0 ? void 0 : _a.fields) !== null && _b !== void 0 ? _b : []);
        setDealItemConfigurationFields((_d = (_c = fieldGroups === null || fieldGroups === void 0 ? void 0 : fieldGroups.fieldGroups.filter((fieldGroup) => fieldGroup.category === 'DEAL_ITEM_CONFIGURATION')[0]) === null || _c === void 0 ? void 0 : _c.fields) !== null && _d !== void 0 ? _d : []);
        appendDealFreezeDebug('effect.fieldGroups.sync.end');
    }, [fieldGroups]);
    const quantityType = (_c = JSON.parse((_b = (_a = configurationFields === null || configurationFields === void 0 ? void 0 : configurationFields.configurationFields.find((conf) => conf.fieldKey === 'QUANTITY_TYPE')) === null || _a === void 0 ? void 0 : _a.value) !== null && _b !== void 0 ? _b : '{}')) === null || _c === void 0 ? void 0 : _c.value;
    const approvalProcess = getConfigurationFieldValue((_d = account.configs) !== null && _d !== void 0 ? _d : [], 'APPROVAL_PROCESS');
    const mewsAccountAsset = canCheckExternalAvailability
        ? (_e = accountAssetsData === null || accountAssetsData === void 0 ? void 0 : accountAssetsData.accountAssets) === null || _e === void 0 ? void 0 : _e.find((a) => {
            var _a, _b, _c;
            return ((_a = a.asset) === null || _a === void 0 ? void 0 : _a.tag) === 'mews' &&
                ((_b = a.connection) === null || _b === void 0 ? void 0 : _b.active) === true &&
                ((_c = a.connection) === null || _c === void 0 ? void 0 : _c.connected) === true;
        })
        : undefined;
    const hasMewsConnector = Boolean(mewsAccountAsset);
    // Fonction pour initialiser les valeurs 'order' manquantes
    const initializeDealOrder = (dealData) => {
        if (!dealData || !dealData.items)
            return dealData;
        const needsInit = dealData.items.some((item) => {
            var _a, _b;
            return ((_a = item.options) === null || _a === void 0 ? void 0 : _a.some((opt) => opt.order === undefined || opt.order === null)) ||
                ((_b = item.packages) === null || _b === void 0 ? void 0 : _b.some((pkg) => pkg.order === undefined || pkg.order === null));
        });
        if (needsInit) {
            return Object.assign(Object.assign({}, dealData), { items: dealData.items.map((item) => {
                    var _a, _b, _c, _d, _e, _f;
                    // Séparer les options normales et proposées
                    const normalOptions = ((_a = item.options) === null || _a === void 0 ? void 0 : _a.filter((opt) => !opt.proposed)) || [];
                    const proposedOptions = ((_b = item.options) === null || _b === void 0 ? void 0 : _b.filter((opt) => opt.proposed)) || [];
                    // Créer un map pour retrouver facilement l'index dans chaque sous-groupe
                    const normalOrderMap = new Map(normalOptions.map((opt, idx) => [opt.id, idx]));
                    const proposedOrderMap = new Map(proposedOptions.map((opt, idx) => [opt.id, idx]));
                    // Séparer les packages normaux et proposés
                    const normalPackages = ((_c = item.packages) === null || _c === void 0 ? void 0 : _c.filter((pkg) => !pkg.proposed)) || [];
                    const proposedPackages = ((_d = item.packages) === null || _d === void 0 ? void 0 : _d.filter((pkg) => pkg.proposed)) || [];
                    const normalPkgOrderMap = new Map(normalPackages.map((pkg, idx) => [pkg.id, idx]));
                    const proposedPkgOrderMap = new Map(proposedPackages.map((pkg, idx) => [pkg.id, idx]));
                    return Object.assign(Object.assign({}, item), { options: (_e = item.options) === null || _e === void 0 ? void 0 : _e.map((opt) => {
                            var _a;
                            return (Object.assign(Object.assign({}, opt), { order: (_a = opt.order) !== null && _a !== void 0 ? _a : (opt.proposed
                                    ? proposedOrderMap.get(opt.id)
                                    : normalOrderMap.get(opt.id)) }));
                        }), packages: (_f = item.packages) === null || _f === void 0 ? void 0 : _f.map((pkg) => {
                            var _a;
                            return (Object.assign(Object.assign({}, pkg), { order: (_a = pkg.order) !== null && _a !== void 0 ? _a : (pkg.proposed
                                    ? proposedPkgOrderMap.get(pkg.id)
                                    : normalPkgOrderMap.get(pkg.id)) }));
                        }) });
                }) });
        }
        return dealData;
    };
    const [deal, setDeal] = useState(() => initializeDealOrder(data === null || data === void 0 ? void 0 : data.deal));
    const [initialDeal, setInitialDeal] = useState(() => initializeDealOrder(data === null || data === void 0 ? void 0 : data.deal));
    const [openModalPublish, setOpenModalPublish] = useState(false);
    const [openModalApprove, setOpenModalApprove] = useState(false);
    const [openMewsBlockModal, setOpenMewsBlockModal] = useState(false);
    const [openReplaceItemsModal, setOpenReplaceItemsModal] = useState(false);
    const [replaceItemsProcessing, setReplaceItemsProcessing] = useState(false);
    const [pendingMewsSaveArgs, setPendingMewsSaveArgs] = useState(null);
    const [isPublish, setIsPublish] = useState(false);
    const [saveDraftProcessing, setSaveDraftProcessing] = useState(false);
    const [saveQuoteProcessing, setSaveQuoteProcessing] = useState(false);
    const [dealUser, setDealUser] = useState((deal === null || deal === void 0 ? void 0 : deal.user) ? deal.user.id : null);
    const [dealPartner, setDealPartner] = useState((deal === null || deal === void 0 ? void 0 : deal.partner) ? deal.partner.id : null);
    const [dealPartnerContact, setDealPartnerContact] = useState((deal === null || deal === void 0 ? void 0 : deal.partnerContact) ? deal.partnerContact.id : null);
    const [language, setLanguage] = useState(deal === null || deal === void 0 ? void 0 : deal.language);
    const [optionAndPackageQuantityAlertShown, setOptionAndPackageQuantityAlertShown] = useState(false);
    const [dealFieldsValues, setDealFieldsValues] = useState({});
    const [openCard, setOpenCard] = useState(false);
    const [selectedButton, setSelectedButton] = useState(undefined);
    const [markupPerItem, setMarkupPerItem] = useState([]);
    const [discountRep, setDiscountRep] = useState(0);
    const [dealTotal, setDealTotal] = useState(0);
    const [saveState, setSaveState] = useState(false);
    const [sendMessage, setSendMessage] = useState(null);
    const [getSaleItem] = useLazyQuery(SaleItemQuery);
    // Track the last deal ID we loaded to avoid overwriting local changes
    const lastLoadedDealId = useRef(null);
    const lastDealDataRef = useRef(null);
    useEffect(() => {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r;
        appendDealFreezeDebug('effect.syncApolloDealIntoState.begin');
        if (data === null || data === void 0 ? void 0 : data.deal) {
            appendDealFreezeDebug(`effect.syncApolloDealIntoState.hasDeal dealId=${data.deal.id}`);
            // Only update deal from cache if:
            // 1. It's the first load (lastLoadedDealId is null)
            // 2. Or it's a different deal (ID changed)
            // This prevents overwriting local unsaved changes when contact is updated
            const isFirstLoad = lastLoadedDealId.current === null;
            const isDifferentDeal = lastLoadedDealId.current !== data.deal.id;
            if (isFirstLoad || isDifferentDeal) {
                const initializedDeal = initializeDealOrder(data.deal);
                setDeal(initializedDeal);
                setInitialDeal(initializedDeal);
                setDealPartner((_b = (_a = data.deal.partner) === null || _a === void 0 ? void 0 : _a.id) !== null && _b !== void 0 ? _b : null);
                setDealPartnerContact((_d = (_c = data.deal.partnerContact) === null || _c === void 0 ? void 0 : _c.id) !== null && _d !== void 0 ? _d : null);
                lastLoadedDealId.current = data.deal.id;
                lastDealDataRef.current = data.deal;
            }
            else {
                // For the same deal, only update contact/client references if they changed
                // This preserves local unsaved changes while updating related entities
                const lastDeal = lastDealDataRef.current;
                if (lastDeal) {
                    const contactChanged = ((_e = data.deal.contact) === null || _e === void 0 ? void 0 : _e.id) !== ((_f = lastDeal.contact) === null || _f === void 0 ? void 0 : _f.id);
                    const clientChanged = ((_g = data.deal.client) === null || _g === void 0 ? void 0 : _g.id) !== ((_h = lastDeal.client) === null || _h === void 0 ? void 0 : _h.id);
                    const partnerChanged = ((_j = data.deal.partner) === null || _j === void 0 ? void 0 : _j.id) !== ((_k = lastDeal.partner) === null || _k === void 0 ? void 0 : _k.id);
                    const partnerContactChanged = ((_l = data.deal.partnerContact) === null || _l === void 0 ? void 0 : _l.id) !== ((_m = lastDeal.partnerContact) === null || _m === void 0 ? void 0 : _m.id);
                    if (contactChanged || clientChanged) {
                        setDeal((prevDeal) => {
                            if (!prevDeal)
                                return prevDeal;
                            return Object.assign(Object.assign({}, prevDeal), { contact: data.deal.contact || prevDeal.contact, client: data.deal.client || prevDeal.client, billTo: data.deal.billTo || prevDeal.billTo, shipTo: data.deal.shipTo || prevDeal.shipTo });
                        });
                    }
                    if (partnerChanged) {
                        setDealPartner((_p = (_o = data.deal.partner) === null || _o === void 0 ? void 0 : _o.id) !== null && _p !== void 0 ? _p : null);
                    }
                    if (partnerContactChanged) {
                        setDealPartnerContact((_r = (_q = data.deal.partnerContact) === null || _q === void 0 ? void 0 : _q.id) !== null && _r !== void 0 ? _r : null);
                    }
                    // Update the ref to track the latest data
                    lastDealDataRef.current = data.deal;
                }
            }
        }
        appendDealFreezeDebug('effect.syncApolloDealIntoState.end');
    }, [data === null || data === void 0 ? void 0 : data.deal]);
    useEffect(() => {
        var _a;
        setDiscountRep(getDiscountRep((_a = user.user) === null || _a === void 0 ? void 0 : _a.contact));
    }, [
        (_g = (_f = user.user) === null || _f === void 0 ? void 0 : _f.contact) === null || _g === void 0 ? void 0 : _g.id,
        (_j = (_h = user.user) === null || _h === void 0 ? void 0 : _h.contact) === null || _j === void 0 ? void 0 : _j.discount,
        (_m = (_l = (_k = user.user) === null || _k === void 0 ? void 0 : _k.contact) === null || _l === void 0 ? void 0 : _l.client) === null || _m === void 0 ? void 0 : _m.id,
        (_q = (_p = (_o = user.user) === null || _o === void 0 ? void 0 : _o.contact) === null || _p === void 0 ? void 0 : _p.client) === null || _q === void 0 ? void 0 : _q.discount
    ]);
    useEffect(() => {
        var _a;
        appendDealFreezeDebug(`effect.inactiveItemsCheck.begin ready=${ready}`);
        // Check if we have any item, variant, option or package inactive or deleted
        if ((_a = data === null || data === void 0 ? void 0 : data.deal) === null || _a === void 0 ? void 0 : _a.items) {
            const inactivatedItems = getInactiveDealItems(data.deal.items);
            if (inactivatedItems.length) {
                alert.present({
                    title: React.createElement("p", null, t('One or more items are inactive. Please check.')),
                    style: 'warning'
                });
            }
        }
        appendDealFreezeDebug('effect.inactiveItemsCheck.end');
    }, [ready]);
    // history.block(..., deal) navigation guard lived here previously as useEffect(..., [deal]). An empty /
    // comment-only body still re-fired on every `deal` identity change → hundreds of effect runs after a
    // single click whenever local state recreated the deal object. Re-enable navigation blocking with deps
    // based on primitives (deal?.id), refs, or a dedicated flag — not `[deal]` alone.
    useEffect(() => {
        var _a, _b, _c;
        appendDealFreezeDebug(`effect.stageSentPublish.begin stage=${(_b = (_a = data === null || data === void 0 ? void 0 : data.deal) === null || _a === void 0 ? void 0 : _a.stage) !== null && _b !== void 0 ? _b : ''}`);
        if (((_c = data === null || data === void 0 ? void 0 : data.deal) === null || _c === void 0 ? void 0 : _c.stage) === 'SENT') {
            setIsPublish(true);
        }
        appendDealFreezeDebug('effect.stageSentPublish.end');
    }, [(_r = data === null || data === void 0 ? void 0 : data.deal) === null || _r === void 0 ? void 0 : _r.stage]);
    useEffect(() => {
        var _a, _b;
        if (!isDealFreezeDebugEnabled())
            return undefined;
        appendDealFreezeDebug(`screen.lifecycle account=${String((_a = account === null || account === void 0 ? void 0 : account.id) !== null && _a !== void 0 ? _a : '?')} tag=${String((_b = account === null || account === void 0 ? void 0 : account.tag) !== null && _b !== void 0 ? _b : '')} routeDealId=${id} canManageAssets=${String(canManageAssets)}`);
        const stopHeartbeat = startDealFreezeHeartbeat();
        return () => {
            appendDealFreezeDebug('screen.lifecycle.cleanup');
            stopHeartbeat === null || stopHeartbeat === void 0 ? void 0 : stopHeartbeat();
            flushDealFreezeDebug();
        };
    }, [account === null || account === void 0 ? void 0 : account.id, account === null || account === void 0 ? void 0 : account.tag, id, canManageAssets]);
    const pastSpinner = Boolean(deal && ready);
    useEffect(() => {
        appendDealFreezeDebug(`pastSpinner.transition=${pastSpinner}`);
    }, [pastSpinner]);
    if (!deal || !ready) {
        return (React.createElement("div", { className: "deal-configure-screen-spinner" },
            React.createElement(Spinner, null)));
    }
    /**
     * @function onClickCard
     * @since 1.0.0
     * @hidden
     */
    const onClickCard = (value) => {
        const actualValue = selectedButton;
        setSelectedButton(value !== actualValue ? value : undefined);
        setOpenCard(value !== actualValue);
    };
    /**
     * @function onCloseCard
     * @since 1.0.0
     * @hidden
     */
    const onCloseCard = () => {
        setOpenCard(false);
        setSelectedButton(undefined);
    };
    /**
     * @function saveDeal
     * @since 1.0.0
     * @hidden
     */
    const saveDeal = (draft, message, sendNotif, skipMarkupCheck = false, createAvailabilityBlock, mewsRateId) => __awaiter(this, void 0, void 0, function* () {
        var _4, _5, _6, _7, _8, _9, _10, _11, _12, _13, _14, _15, _16, _17;
        if (deal) {
            let status = deal.status;
            let stage = deal.stage;
            // Check if we have at least 1 markup greater than 0
            if (!skipMarkupCheck) {
                const presentMarkup = markupPerItem.some((markup) => {
                    return markup.value > 0;
                });
                if (!presentMarkup) {
                    modal.present({
                        style: 'dialog',
                        title: t('Confirm'),
                        picto: 'times',
                        modal: (React.createElement(Confirm, { title: t('Missing markup'), infos: t('You are saving a deal without markup.'), onAccept: () => {
                                saveDeal(draft, message, sendNotif, true);
                            } }))
                    });
                }
                else {
                    saveDeal(draft, message, sendNotif, true);
                }
            }
            else {
                const items = toUpdateDealItemInput(deal.items);
                if (draft) {
                    setSaveDraftProcessing(true);
                    if (account.tag == 'alpha') {
                        stage = 'DRAFT';
                        status = 'ACTIVE';
                    }
                }
                else {
                    setSaveQuoteProcessing(true);
                    if (approvalProcess == 'TRUE') {
                        stage = 'PENDING_APPROVAL';
                        status = 'INACTIVE';
                    }
                    else if (status === 'WON' && sendNotif) {
                        status = 'ACTIVE';
                        stage = 'SENT';
                    }
                    else if (stage === 'SENT' && sendNotif) {
                        stage = 'SENT';
                    }
                    else if (sendNotif) {
                        stage = 'SENT';
                        status = 'ACTIVE';
                    }
                    else {
                        stage = 'READY_TO_SEND';
                        status = 'ACTIVE';
                    }
                }
                if (status === 'INACTIVE' &&
                    hasMewsConnector &&
                    createAvailabilityBlock === undefined &&
                    deal.plannedDateStart) {
                    setPendingMewsSaveArgs({ draft, message, sendNotif });
                    setOpenMewsBlockModal(true);
                    setSaveQuoteProcessing(false);
                    return;
                }
                const translations = [];
                account.languages.forEach((language) => {
                    var _a, _b, _c;
                    translations.push({
                        language: language.code,
                        headline: language.code == deal.language
                            ? deal.headline
                            : (_a = deal.translations.find((translation) => translation.language.code === language.code)) === null || _a === void 0 ? void 0 : _a.headline,
                        introduction: language.code == deal.language
                            ? deal.introduction
                            : (_b = deal.translations.find((translation) => translation.language.code === language.code)) === null || _b === void 0 ? void 0 : _b.introduction,
                        termsAndConditions: language.code == deal.language
                            ? deal.termsAndConditions
                            : (_c = deal.translations.find((translation) => translation.language.code === language.code)) === null || _c === void 0 ? void 0 : _c.termsAndConditions
                    });
                });
                try {
                    yield apollo.mutate({
                        mutation: UpdateDealMutation,
                        variables: {
                            input: {
                                id: deal.id,
                                draft: false,
                                title: deal.title,
                                language: language,
                                status: status,
                                stage: stage,
                                plannedDate: resolvePlannedDateForUpdate(deal),
                                items,
                                client: (_5 = (_4 = deal.client) === null || _4 === void 0 ? void 0 : _4.id) !== null && _5 !== void 0 ? _5 : null,
                                contact: (_7 = (_6 = deal.contact) === null || _6 === void 0 ? void 0 : _6.id) !== null && _7 !== void 0 ? _7 : null,
                                billTo: (_9 = (_8 = deal.billTo) === null || _8 === void 0 ? void 0 : _8.id) !== null && _9 !== void 0 ? _9 : null,
                                shipTo: (_11 = (_10 = deal.shipTo) === null || _10 === void 0 ? void 0 : _10.id) !== null && _11 !== void 0 ? _11 : null,
                                discount: deal.discount,
                                discountType: deal.discountType,
                                otherFees: (_12 = deal.otherFees) === null || _12 === void 0 ? void 0 : _12.map((otherFee) => {
                                    var _a, _b;
                                    return ({
                                        description: otherFee.description,
                                        price: otherFee.price,
                                        fee: (_b = (_a = otherFee.fee) === null || _a === void 0 ? void 0 : _a.id) !== null && _b !== void 0 ? _b : null
                                    });
                                }),
                                payments: (_13 = deal.payments) === null || _13 === void 0 ? void 0 : _13.map((payment) => {
                                    var _a, _b, _c;
                                    return ({
                                        id: (_a = payment.id) !== null && _a !== void 0 ? _a : null,
                                        date: payment.date,
                                        description: payment.description,
                                        amount: payment.amount,
                                        percent: (_b = payment.percent) !== null && _b !== void 0 ? _b : null,
                                        status: payment.status,
                                        isDeposit: (_c = payment.isDeposit) !== null && _c !== void 0 ? _c : null
                                    });
                                }),
                                taxAmount: deal.taxAmount,
                                internalNotes: deal.internalNotes,
                                notes: deal.notes,
                                showIncludedOptions: deal.showIncludedOptions,
                                fieldValues: deal.fieldValues
                                    ? deal.fieldValues.map((f) => {
                                        return { fieldId: f.field.id, value: f.value };
                                    })
                                    : [],
                                translations,
                                sendNotif,
                                message: sendMessage,
                                assignTo: dealUser,
                                partner: dealPartner,
                                partnerContact: dealPartnerContact,
                                newVersion: false,
                                currency: deal.currency.id,
                                term: (_15 = (_14 = deal.term) === null || _14 === void 0 ? void 0 : _14.id) !== null && _15 !== void 0 ? _15 : null,
                                validityPeriod: deal.validityPeriod,
                                priceDisplay: deal.priceDisplay,
                                canGeneratePDF: deal.canGeneratePDF,
                                canEditQuantity: deal.canEditQuantity,
                                canBeSigned: deal.canBeSigned,
                                showDiscount: deal.showDiscount,
                                medias: (_17 = (_16 = deal.medias) === null || _16 === void 0 ? void 0 : _16.map((media) => {
                                    var _a;
                                    return {
                                        media: (_a = media.media) === null || _a === void 0 ? void 0 : _a.id,
                                        type: media.type,
                                        url: media.url
                                    };
                                })) !== null && _17 !== void 0 ? _17 : null,
                                createAvailabilityBlock: createAvailabilityBlock !== null && createAvailabilityBlock !== void 0 ? createAvailabilityBlock : false,
                                mewsRateId: mewsRateId !== null && mewsRateId !== void 0 ? mewsRateId : null
                            }
                        }
                    });
                    alert.present({
                        title: React.createElement("p", null, message)
                    });
                }
                catch (error) {
                    console.error(error);
                    // Montre le message du serveur (ex. item déjà réservé) ; la
                    // soumission reste à sauvegarder.
                    alert.present({ error });
                    setSaveDraftProcessing(false);
                    setSaveQuoteProcessing(false);
                    return;
                }
                setSaveDraftProcessing(false);
                setSaveQuoteProcessing(false);
                setOpenModalPublish(false);
                setInitialDeal(deal);
                setSaveState(true);
            }
        }
    });
    /**
     * @function onSaveDraft
     * @since 1.0.0
     * @hidden
     */
    const onSaveDraft = () => {
        saveDeal(true, t('Your deal have been saved as draft.'), false, true);
    };
    /**
     * @function onPublish
     * @since 1.0.0
     * @hidden
     */
    const onPublish = (sendNotif) => {
        saveDeal(false, sendNotif
            ? t('Your deal have been saved and sent to the client.')
            : t('Your deal have been saved.'), sendNotif, true);
    };
    /**
     * @function onOpenApprovalModal
     * @since 1.0.0
     * @hidden
     */
    const onOpenApprovalModal = () => {
        setOpenModalApprove(true);
    };
    /**
     * @function onOpenApprovalModal
     * @since 1.0.0
     * @hidden
     */
    const onCloseApprovalModal = () => {
        setOpenModalApprove(false);
    };
    /**
     * @function onConfirmApprove
     * @since 1.0.0
     * @hidden
     */
    const onConfirmApprove = () => {
        onSubmitApprove();
        onCloseApprovalModal();
    };
    /**
     * @function onSubmitApprove
     * @since 1.0.0
     * @hidden
     */
    const onSubmitApprove = () => __awaiter(this, void 0, void 0, function* () {
        try {
            if (data === null || data === void 0 ? void 0 : data.deal) {
                yield apollo.mutate({
                    mutation: UpdateDealMutation,
                    variables: {
                        input: {
                            id: data === null || data === void 0 ? void 0 : data.deal.id,
                            status: 'ACTIVE',
                            stage: 'APPROVED',
                            newVersion: false
                        }
                    },
                    refetchQueries: [
                        {
                            query: DealQuery,
                            variables: {
                                input: {
                                    id: data === null || data === void 0 ? void 0 : data.deal.id
                                }
                            }
                        }
                    ]
                });
                alert.present({
                    title: React.createElement("p", null, t('This deal was approved.'))
                });
            }
        }
        catch (error) {
            alert.present({ error });
        }
    });
    /**
     * @function onPressOpenModalPublish
     * @since 1.0.0
     * @hidden
     */
    const onPressOpenModalPublish = () => {
        var _a;
        // Direct sales have their payment synced by the server, and someone
        // without access to payments could not fix the schedule.
        const payments = (_a = deal === null || deal === void 0 ? void 0 : deal.payments) !== null && _a !== void 0 ? _a : [];
        const gap = payments.length > 0 &&
            !(deal === null || deal === void 0 ? void 0 : deal.directSale) &&
            user.can('deal.payment.manage')
            ? paymentScheduleBalance(payments.filter((payment) => !isPendingUpsellPayment(payment)), dealTotal)
            : 0;
        if (Math.abs(gap) < 0.005) {
            setOpenModalPublish(true);
            return;
        }
        modal.present({
            style: 'dialog',
            title: t('Confirm'),
            picto: 'triangle-exclamation',
            modal: (React.createElement(Confirm, { title: t('Payments do not match the total'), infos: t('The payments differ from the total by {{amount}}.').replace('{{amount}}', toMoney(Math.abs(gap))), acceptText: t('Continue'), onAccept: () => setOpenModalPublish(true) }))
        });
    };
    /**
     * @function onCloseModalPublish
     * @since 1.0.0
     * @hidden
     */
    const onCloseModalPublish = () => {
        setOpenModalPublish(false);
    };
    // CONTEXT -------------------------------------------------------------------
    const pricingParameters = getConfigurationFieldValue(configurationFields === null || configurationFields === void 0 ? void 0 : configurationFields.configurationFields, 'PRICING_PARAMETERS');
    const pricingParametersDisplay = getConfigurationFieldValue(configurationFields === null || configurationFields === void 0 ? void 0 : configurationFields.configurationFields, 'PRICING_PARAMETERS_DISPLAY');
    const hasDealItemConfigurationTime = getConfigurationFieldValue(configurationFields === null || configurationFields === void 0 ? void 0 : configurationFields.configurationFields, 'DEAL_ITEM_CONFIGURATION_TIME') === 'TRUE';
    const globalMarkup = 0;
    let itemHeaderType = 'COMPLEX';
    itemHeaderType = getConfigurationFieldValue(configurationFields === null || configurationFields === void 0 ? void 0 : configurationFields.configurationFields, 'ITEM_HEADER_TYPE');
    // TODO: Remove this when we have a better way to handle this (use config field)
    let optionReplacement = 'none';
    let primaryGroup = 'none';
    if (account.tag == 'mrgcanada') {
        //optionReplacement = 'code';
    }
    if (account.tag == 'motrec') {
        primaryGroup = 'Colors';
    }
    // TODO : replace this by a global variable
    let itemsDisplay = 'ITEM_ONLY';
    if (account.tag == 'motrec') {
        itemsDisplay = 'VARIANT_GROUPED';
    }
    /* -- Configure Item -- */
    /**
     * @function addDealItem
     * @since 1.0.0
     */
    const addDealItem = (item, variant, options, packages, quantity, alternativeTo) => {
        var _a, _b, _c, _d, _e;
        const newId = uuidv4();
        // The client's price list replaces the default price of the product
        const priceListContext = {
            clientId: (_a = deal.client) === null || _a === void 0 ? void 0 : _a.id,
            userId: (_c = (_b = deal.user) === null || _b === void 0 ? void 0 : _b.id) !== null && _c !== void 0 ? _c : (_d = user.user) === null || _d === void 0 ? void 0 : _d.id
        };
        const clientList = defaultDealPriceList(priceListsData === null || priceListsData === void 0 ? void 0 : priceListsData.priceLists, priceListContext, item.id, variant === null || variant === void 0 ? void 0 : variant.id);
        const basePrice = (_e = findPriceListPrice(clientList, item.id, variant === null || variant === void 0 ? void 0 : variant.id)) !== null && _e !== void 0 ? _e : (variant ? variant.price : item.price);
        setDeal(Object.assign(Object.assign({}, deal), { items: [
                ...deal.items,
                {
                    id: newId,
                    quantity: quantity,
                    price: calculatePriceCurrency(basePrice !== null && basePrice !== void 0 ? basePrice : 0, deal.currency) *
                        (1 - discountRep / 100),
                    createdAt: item.createdAt,
                    updatedAt: item.updatedAt,
                    item: item,
                    initiallyProposed: false,
                    alternativeTo: alternativeTo,
                    order: deal.items.length,
                    variant: variant ? variant : null,
                    proposed: alternativeTo ? true : false,
                    options: options.map((option) => {
                        var _a, _b, _c, _d;
                        return (Object.assign(Object.assign({}, option), { id: uuidv4(), price: calculatePriceCurrency((_a = option.price) !== null && _a !== void 0 ? _a : 0, deal.currency) *
                                (1 - discountRep / 100), quantity: (_b = option.quantity) !== null && _b !== void 0 ? _b : 1, type: 'OPTION', priceDisplay: (_c = option.priceDisplay) !== null && _c !== void 0 ? _c : 'AMOUNT', hide: (_d = option.hide) !== null && _d !== void 0 ? _d : false, initiallyProposed: option.proposed, alternativeTo: option.alternativeTo, priceList: option.priceList, createdAt: option.item.createdAt, updatedAt: option.item.updatedAt }));
                    }),
                    packages: packages.map((pack) => {
                        var _a, _b, _c, _d;
                        return (Object.assign(Object.assign({}, pack), { id: uuidv4(), price: calculatePriceCurrency(pack.package.price, deal.currency) *
                                (1 - discountRep / 100), quantity: pack.quantity, type: 'PACKAGE', priceDisplay: (_a = pack.priceDisplay) !== null && _a !== void 0 ? _a : 'AMOUNT', hide: (_b = pack.hide) !== null && _b !== void 0 ? _b : false, createdAt: pack.package.createdAt, updatedAt: pack.package.updatedAt, package: pack.package, proposed: pack.proposed, fieldValues: (_c = pack.fieldValues) !== null && _c !== void 0 ? _c : [], relatedItems: (_d = pack.package.saleItems) === null || _d === void 0 ? void 0 : _d.map((saleItem) => {
                                var _a, _b, _c;
                                return ({
                                    id: uuidv4(),
                                    price: calculatePriceCurrency((_a = saleItem.item.price) !== null && _a !== void 0 ? _a : 0, deal.currency) *
                                        (1 - discountRep / 100),
                                    quantity: 1,
                                    type: 'OPTION',
                                    priceDisplay: (_b = pack.priceDisplay) !== null && _b !== void 0 ? _b : 'AMOUNT',
                                    hide: (_c = pack.hide) !== null && _c !== void 0 ? _c : false,
                                    createdAt: saleItem.item.createdAt,
                                    updatedAt: saleItem.item.updatedAt,
                                    item: saleItem.item
                                });
                            }) }));
                    })
                }
            ] }));
        // scroll to item
        const element = document.getElementById('#' + newId);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
        }
    };
    /**
     * @function configureItem
     * @since 1.0.0
     */
    const configureItem = (_saleItem) => __awaiter(this, void 0, void 0, function* () {
        let step = 0;
        let defaultVariant = null;
        const { data: saleItemResult } = yield getSaleItem({
            variables: { input: { id: _saleItem.id } }
        });
        const saleItem = saleItemResult === null || saleItemResult === void 0 ? void 0 : saleItemResult.saleItem;
        if (!saleItem) {
            return;
        }
        if (!saleItem.variants.length) {
            step = 2;
            if (!saleItem.packages.length ||
                !saleItem.packages.filter((pack) => pack.package.active).length) {
                step = 3;
            }
        }
        else if (saleItem.variants.length == 1) {
            defaultVariant = saleItem.variants[0];
            if (!saleItem.packages.length ||
                !saleItem.packages.filter((pack) => pack.package.active).length) {
                step = 3;
            }
            step = 2;
        }
        const { item, variant, options, packages, quantity } = yield presentPicker(modal, {
            deal,
            item: saleItem,
            variant: defaultVariant,
            label: t('Add To Quote'),
            step,
            generateOptions: true,
            optionReplacement,
            primaryGroup,
            itemsDisplay,
            skipContact: true
        });
        addDealItem(item, variant, options, packages, quantity);
    });
    /**
     * @function browsePicker
     * @since 1.0.0
     */
    const browsePicker = (skipContact) => __awaiter(this, void 0, void 0, function* () {
        const { item, variant, options, packages, quantity } = yield presentPicker(modal, {
            deal,
            label: t('Add To Quote'),
            optionReplacement,
            primaryGroup,
            itemsDisplay,
            skipContact
        });
        addDealItem(item, variant, options, packages, quantity);
    });
    /* -- Price Changes -- */
    /**
     * @function mergeConfigurationPrices
     * Merge price updates without dropping configurations missing from incoming data.
     * @since 1.0.0
     * @hidden
     */
    const mergeConfigurationPrices = (current, incoming) => {
        if (!current) {
            return current;
        }
        if (!incoming) {
            return current;
        }
        return current.map((entry) => {
            const updated = incoming.find((option) => option.id === entry.id);
            if (!updated) {
                return entry;
            }
            return Object.assign(Object.assign({}, entry), { price: updated.price });
        });
    };
    /**
     * @function onItemPriceChange
     * @since 1.0.0
     */
    const onItemPriceChange = (dealItem, value, type) => {
        setDeal((prevDeal) => {
            var _a, _b, _c, _d, _e, _f, _g;
            if (!prevDeal) {
                return prevDeal;
            }
            const dealItemIndex = prevDeal.items.findIndex((item) => item.id === dealItem.id);
            if (dealItemIndex === -1) {
                return prevDeal;
            }
            const currentItem = prevDeal.items[dealItemIndex];
            let updatedItem = Object.assign(Object.assign({}, currentItem), { price: (_a = dealItem.price) !== null && _a !== void 0 ? _a : currentItem.price, options: mergeConfigurationPrices(currentItem.options, dealItem.options), packages: mergeConfigurationPrices(currentItem.packages, dealItem.packages) });
            if (type === 'MARKUP') {
                if (value != null && pricingParametersDisplay == 'item') {
                    const updatedPackage = (_b = currentItem.packages) === null || _b === void 0 ? void 0 : _b.map((pack) => {
                        const packPrice = pack.package ? pack.package.price : pack.price;
                        const pricePerUnitValue = getPricePerUnitFromMarkup(packPrice - (packPrice * discountRep) / 100, Number(value));
                        return Object.assign(Object.assign({}, pack), { price: pricePerUnitValue });
                    });
                    const updatedOption = (_c = currentItem.options) === null || _c === void 0 ? void 0 : _c.map((option) => {
                        var _a, _b;
                        const optionPrice = getPriceFromItem(currentItem.item, (_a = currentItem.variant) !== null && _a !== void 0 ? _a : null, (_b = option.item) !== null && _b !== void 0 ? _b : null, 'option', prevDeal.currency);
                        const pricePerUnitValue = getPricePerUnitFromMarkup(optionPrice ? optionPrice - (optionPrice * discountRep) / 100 : 0, Number(value));
                        return Object.assign(Object.assign({}, option), { price: pricePerUnitValue });
                    });
                    updatedItem = Object.assign(Object.assign({}, currentItem), { packages: updatedPackage, options: updatedOption });
                }
            }
            else if (type === 'DISCOUNT') {
                if (value != null) {
                    const updatedPackage = (_d = currentItem.packages) === null || _d === void 0 ? void 0 : _d.map((pack) => {
                        const packPrice = pack.package ? pack.package.price : pack.price;
                        const dealerPackPrice = packPrice - (packPrice * discountRep) / 100;
                        const pricePerUnitValue = dealerPackPrice - (dealerPackPrice * Number(value)) / 100;
                        return Object.assign(Object.assign({}, pack), { price: pricePerUnitValue });
                    });
                    const updatedOption = (_e = currentItem.options) === null || _e === void 0 ? void 0 : _e.map((option) => {
                        var _a, _b;
                        const catalogPrice = getPriceFromItem(currentItem.item, (_a = currentItem.variant) !== null && _a !== void 0 ? _a : null, (_b = option.item) !== null && _b !== void 0 ? _b : null, 'option', prevDeal.currency);
                        const dealerOptionPrice = catalogPrice
                            ? catalogPrice - (catalogPrice * discountRep) / 100
                            : 0;
                        const pricePerUnitValue = dealerOptionPrice
                            ? dealerOptionPrice - (dealerOptionPrice * Number(value)) / 100
                            : 0;
                        return Object.assign(Object.assign({}, option), { price: pricePerUnitValue });
                    });
                    updatedItem = Object.assign(Object.assign({}, currentItem), { packages: updatedPackage, options: updatedOption });
                }
            }
            else if (type === 'MARGIN') {
                if (value != null) {
                    const updatedPackage = (_f = currentItem.packages) === null || _f === void 0 ? void 0 : _f.map((pack) => {
                        const packPrice = pack.package ? pack.package.price : pack.price;
                        const pricePerUnitValue = getPricePerUnitFromMargin(packPrice - (packPrice * discountRep) / 100, Number(value));
                        return Object.assign(Object.assign({}, pack), { price: pricePerUnitValue });
                    });
                    const updatedOption = (_g = currentItem.options) === null || _g === void 0 ? void 0 : _g.map((option) => {
                        var _a, _b;
                        const optionPrice = getPriceFromItem(currentItem.item, (_a = currentItem.variant) !== null && _a !== void 0 ? _a : null, (_b = option.item) !== null && _b !== void 0 ? _b : null, 'option', prevDeal.currency);
                        const pricePerUnitValue = getPricePerUnitFromMargin(optionPrice ? optionPrice - (optionPrice * discountRep) / 100 : 0, Number(value));
                        return Object.assign(Object.assign({}, option), { price: pricePerUnitValue });
                    });
                    updatedItem = Object.assign(Object.assign({}, currentItem), { packages: updatedPackage, options: updatedOption });
                }
            }
            const tempDealItems = [...prevDeal.items];
            tempDealItems[dealItemIndex] = updatedItem;
            return Object.assign(Object.assign({}, prevDeal), { items: tempDealItems });
        });
    };
    /**
     * @function setSubtotal
     * @since 1.0.0
     * */
    const setSubtotal = (value) => {
        setDeal((prev) => {
            if (!prev || prev.subtotal === value) {
                return prev;
            }
            return Object.assign(Object.assign({}, prev), { subtotal: value });
        });
    };
    /**
     * @function setTotal
     * @since 1.0.0
     */
    const setTotal = (value) => {
        setDealTotal(value);
        // Percent-based payments follow the total as products, taxes or the
        // discount change.
        setDeal((prevDeal) => {
            const payments = prevDeal === null || prevDeal === void 0 ? void 0 : prevDeal.payments;
            if (!prevDeal || !(payments === null || payments === void 0 ? void 0 : payments.length)) {
                return prevDeal;
            }
            const synced = applyPercentPaymentAmounts(payments, value);
            const changed = synced.some((payment, index) => payment.amount !== payments[index].amount);
            return changed ? Object.assign(Object.assign({}, prevDeal), { payments: synced }) : prevDeal;
        });
    };
    /**
     * @function updateCurrency
     * @since 1.0.0
     */
    const updateCurrency = (currency) => {
        const tempDealItems = deal.items.map((dealItem) => {
            var _a, _b, _c, _d;
            const updatedPackage = (_a = dealItem.packages) === null || _a === void 0 ? void 0 : _a.map((pack) => {
                const packPrice = pack.package ? pack.package.price : pack.price;
                const pricePerUnitValue = getPricePerUnitFromMarkup(packPrice - (packPrice * discountRep) / 100, 0);
                return Object.assign(Object.assign({}, pack), { price: calculatePriceCurrency(pricePerUnitValue, currency) });
            });
            const updatedOption = (_b = dealItem.options) === null || _b === void 0 ? void 0 : _b.map((option) => {
                var _a, _b;
                const optionPrice = getPriceFromItem(dealItem.item, (_a = dealItem.variant) !== null && _a !== void 0 ? _a : null, (_b = option.item) !== null && _b !== void 0 ? _b : null, 'option', currency);
                const pricePerUnitValue = getPricePerUnitFromMarkup(optionPrice ? optionPrice - (optionPrice * discountRep) / 100 : 0, 0);
                return Object.assign(Object.assign({}, option), { price: pricePerUnitValue });
            });
            return Object.assign(Object.assign({}, dealItem), { price: calculatePriceCurrency(dealItem.variant
                    ? ((_c = dealItem.variant.price) !== null && _c !== void 0 ? _c : 0)
                    : ((_d = dealItem.item.price) !== null && _d !== void 0 ? _d : 0), currency), packages: updatedPackage, options: updatedOption });
        });
        setDeal(Object.assign(Object.assign({}, deal), { currency, items: tempDealItems }));
    };
    /* -- Update Deal -- */
    /**
     * @function updateDealItem
     * @since 1.0.0
     */
    const updateDealItem = (dealItem, item, variant, options, packages) => {
        var _a;
        const tempDealItems = [...deal.items];
        const dealItemIndex = deal.items.findIndex((item) => item.id === dealItem.id);
        tempDealItems[dealItemIndex] = Object.assign(Object.assign({}, tempDealItems[dealItemIndex]), { item,
            variant, price: calculatePriceCurrency((variant === null || variant === void 0 ? void 0 : variant.price) ? variant.price : ((_a = dealItem.price) !== null && _a !== void 0 ? _a : 0), deal.currency), options: options.map((option) => {
                var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q;
                const previous = (_a = dealItem.options) === null || _a === void 0 ? void 0 : _a.find((entry) => { var _a; return ((_a = entry.item) === null || _a === void 0 ? void 0 : _a.id) === option.item.id; });
                return Object.assign(Object.assign(Object.assign({}, previous), option), { id: (_b = previous === null || previous === void 0 ? void 0 : previous.id) !== null && _b !== void 0 ? _b : uuidv4(), price: typeof option.price === 'number'
                        ? option.price
                        : calculatePriceCurrency((_c = option.item.price) !== null && _c !== void 0 ? _c : 0, deal.currency), quantity: (_e = (_d = option.quantity) !== null && _d !== void 0 ? _d : previous === null || previous === void 0 ? void 0 : previous.quantity) !== null && _e !== void 0 ? _e : 1, type: 'OPTION', priceDisplay: (_g = (_f = option.priceDisplay) !== null && _f !== void 0 ? _f : previous === null || previous === void 0 ? void 0 : previous.priceDisplay) !== null && _g !== void 0 ? _g : 'AMOUNT', hide: (_j = (_h = option.hide) !== null && _h !== void 0 ? _h : previous === null || previous === void 0 ? void 0 : previous.hide) !== null && _j !== void 0 ? _j : false, startTime: (_l = (_k = option.startTime) !== null && _k !== void 0 ? _k : previous === null || previous === void 0 ? void 0 : previous.startTime) !== null && _l !== void 0 ? _l : null, endTime: (_o = (_m = option.endTime) !== null && _m !== void 0 ? _m : previous === null || previous === void 0 ? void 0 : previous.endTime) !== null && _o !== void 0 ? _o : null, fieldValues: (_q = (_p = option.fieldValues) !== null && _p !== void 0 ? _p : previous === null || previous === void 0 ? void 0 : previous.fieldValues) !== null && _q !== void 0 ? _q : null, createdAt: option.item.createdAt, updatedAt: option.item.updatedAt });
            }), packages: packages.map((pack) => {
                var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o;
                const previous = (_a = dealItem.packages) === null || _a === void 0 ? void 0 : _a.find((entry) => { var _a; return ((_a = entry.package) === null || _a === void 0 ? void 0 : _a.id) === pack.package.id; });
                return Object.assign(Object.assign(Object.assign({}, previous), pack), { id: (_b = previous === null || previous === void 0 ? void 0 : previous.id) !== null && _b !== void 0 ? _b : uuidv4(), price: typeof pack.price === 'number'
                        ? pack.price
                        : calculatePriceCurrency(pack.package.price, deal.currency), quantity: pack.quantity, type: 'PACKAGE', priceDisplay: (_d = (_c = pack.priceDisplay) !== null && _c !== void 0 ? _c : previous === null || previous === void 0 ? void 0 : previous.priceDisplay) !== null && _d !== void 0 ? _d : 'AMOUNT', hide: (_f = (_e = pack.hide) !== null && _e !== void 0 ? _e : previous === null || previous === void 0 ? void 0 : previous.hide) !== null && _f !== void 0 ? _f : false, startTime: (_h = (_g = pack.startTime) !== null && _g !== void 0 ? _g : previous === null || previous === void 0 ? void 0 : previous.startTime) !== null && _h !== void 0 ? _h : null, endTime: (_k = (_j = pack.endTime) !== null && _j !== void 0 ? _j : previous === null || previous === void 0 ? void 0 : previous.endTime) !== null && _k !== void 0 ? _k : null, fieldValues: (_m = (_l = pack.fieldValues) !== null && _l !== void 0 ? _l : previous === null || previous === void 0 ? void 0 : previous.fieldValues) !== null && _m !== void 0 ? _m : null, createdAt: pack.package.createdAt, updatedAt: pack.package.updatedAt, package: pack.package, proposed: pack.proposed, relatedItems: (_o = pack.package.saleItems) === null || _o === void 0 ? void 0 : _o.map((saleItem) => {
                        var _a, _b;
                        return ({
                            id: uuidv4(),
                            price: calculatePriceCurrency((_a = saleItem.item.price) !== null && _a !== void 0 ? _a : 0, deal.currency),
                            quantity: 1,
                            type: 'OPTION',
                            priceDisplay: 'AMOUNT',
                            hide: (_b = pack.hide) !== null && _b !== void 0 ? _b : false,
                            createdAt: saleItem.item.createdAt,
                            updatedAt: saleItem.item.updatedAt,
                            item: saleItem.item
                        });
                    }) });
            }) });
        setDeal(Object.assign(Object.assign({}, deal), { items: tempDealItems }));
    };
    /* -- Item -- */
    /**
     * @function updateItem
     * @since 1.0.0
     */
    const updateItem = (dealItem) => __awaiter(this, void 0, void 0, function* () {
        var _18, _19;
        const { item, variant, options, packages } = yield presentPicker(modal, {
            deal,
            item: dealItem.item,
            variant: dealItem.variant,
            options: getSelectedOptions(dealItem),
            packages: getSelectedPackages(dealItem),
            label: t('Update Quote'),
            step: ((_18 = dealItem.item.variants) === null || _18 === void 0 ? void 0 : _18.some((variant) => variant.active))
                ? 1
                : ((_19 = dealItem.item.packages) === null || _19 === void 0 ? void 0 : _19.length)
                    ? 2
                    : 3,
            generateOptions: true,
            optionReplacement,
            primaryGroup,
            itemsDisplay
        });
        updateDealItem(dealItem, item, variant, options, packages);
    });
    /**
     * @function onItemQuantityChange
     * @since 1.0.0
     * @hidden
     */
    const onItemQuantityChange = (dealItem, quantity, applyToAll) => {
        var _a, _b, _c;
        const index = deal.items.findIndex((entry) => entry.id == dealItem.id);
        if (index === -1) {
            return;
        }
        const tempDealItems = [...deal.items];
        let tempDealItem = tempDealItems[index];
        const tempOptions = [];
        const tempPackages = [];
        for (const option of (_a = tempDealItem.options) !== null && _a !== void 0 ? _a : []) {
            tempOptions.push(Object.assign(Object.assign({}, option), { quantity }));
        }
        for (const pack of (_b = tempDealItem.packages) !== null && _b !== void 0 ? _b : []) {
            tempPackages.push(Object.assign(Object.assign({}, pack), { quantity }));
        }
        tempDealItem = Object.assign(Object.assign({}, tempDealItem), { quantity, options: quantityType === 'GLOBAL' || applyToAll
                ? tempOptions
                : tempDealItem.options, packages: quantityType === 'GLOBAL' || applyToAll
                ? tempPackages
                : tempDealItem.packages });
        tempDealItems[index] = tempDealItem;
        if (account.tag == 'motrec') {
            let newDiscountRep = getDiscountRep((_c = user.user) === null || _c === void 0 ? void 0 : _c.contact);
            if (quantity >= 5 && quantity <= 9) {
                newDiscountRep += 1.5;
            }
            else if (quantity >= 10 && quantity <= 14) {
                newDiscountRep += 2.5;
            }
            else if (quantity >= 15) {
                newDiscountRep += 3.5;
            }
            setDiscountRep(newDiscountRep);
        }
        setDeal(Object.assign(Object.assign({}, deal), { items: tempDealItems }));
    };
    /**
     * @function onItemDateChange
     * @since 1.0.0
     * @hidden
     */
    const onItemDateChange = (dealItem, date) => {
        const index = deal.items.findIndex((entry) => entry.id == dealItem.id);
        if (index === -1) {
            return;
        }
        const tempDealItems = [...deal.items];
        tempDealItems[index] = Object.assign(Object.assign({}, tempDealItems[index]), { date });
        setDeal(Object.assign(Object.assign({}, deal), { items: tempDealItems }));
    };
    /**
     * @function onMoveItem
     * @since 1.0.0
     * @hidden
     */
    const onMoveItem = (dealItem, direction) => {
        const index = deal.items.findIndex((entry) => entry.id == dealItem.id);
        if (index === -1) {
            return;
        }
        const tempDealItems = [...deal.items];
        const tempDealItem = tempDealItems[index];
        tempDealItems.splice(index, 1);
        if (direction === 'up') {
            tempDealItems.splice(index - 1, 0, tempDealItem);
        }
        else {
            tempDealItems.splice(index + 1, 0, tempDealItem);
        }
        setDeal(Object.assign(Object.assign({}, deal), { items: tempDealItems }));
    };
    /**
     * @function onMoveItemConfiguration
     * @since 1.0.0
     * @hidden
     */
    const onMoveItemConfiguration = (dealItem, configuration, type, direction) => {
        var _a, _b, _c, _d, _e, _f, _g, _h;
        const index = deal.items.findIndex((entry) => entry.id == dealItem.id);
        if (index === -1) {
            return;
        }
        const tempDealItems = [...deal.items];
        const tempDealItem = tempDealItems[index];
        if (type === 'option') {
            // Separate normal and proposed options before moving
            const normalOptions = [
                ...((_b = (_a = tempDealItem.options) === null || _a === void 0 ? void 0 : _a.filter((opt) => !opt.proposed)) !== null && _b !== void 0 ? _b : [])
            ];
            const proposedOptions = [
                ...((_d = (_c = tempDealItem.options) === null || _c === void 0 ? void 0 : _c.filter((opt) => opt.proposed)) !== null && _d !== void 0 ? _d : [])
            ];
            // Determine which list the configuration belongs to
            const isProposed = proposedOptions.some((entry) => entry.id === configuration.id);
            const targetOptions = isProposed ? proposedOptions : normalOptions;
            // Find the option index within its group
            const optionIndex = targetOptions.findIndex((entry) => entry.id === configuration.id);
            if (optionIndex === -1) {
                return;
            }
            // Check if the move is possible within the group
            if (direction === 'up' && optionIndex === 0) {
                return;
            }
            if (direction === 'down' && optionIndex === targetOptions.length - 1) {
                return;
            }
            // Move the option within its group
            const tempOption = targetOptions[optionIndex];
            targetOptions.splice(optionIndex, 1);
            if (tempOption) {
                const newIndex = direction === 'up' ? optionIndex - 1 : optionIndex + 1;
                targetOptions.splice(newIndex, 0, tempOption);
                // Update 'order' property for normal options
                const updatedNormalOptions = (isProposed ? normalOptions : targetOptions).map((option, idx) => (Object.assign(Object.assign({}, option), { order: idx })));
                // Update 'order' property for proposed options
                const updatedProposedOptions = (isProposed ? targetOptions : proposedOptions).map((option, idx) => (Object.assign(Object.assign({}, option), { order: idx })));
                // Recombine: normal first, then proposed
                const allOptions = [...updatedNormalOptions, ...updatedProposedOptions];
                tempDealItems[index] = Object.assign(Object.assign({}, tempDealItem), { options: allOptions });
            }
        }
        else {
            // Separate normal and proposed packages before moving
            const normalPackages = [
                ...((_f = (_e = tempDealItem.packages) === null || _e === void 0 ? void 0 : _e.filter((pkg) => !pkg.proposed)) !== null && _f !== void 0 ? _f : [])
            ];
            const proposedPackages = [
                ...((_h = (_g = tempDealItem.packages) === null || _g === void 0 ? void 0 : _g.filter((pkg) => pkg.proposed)) !== null && _h !== void 0 ? _h : [])
            ];
            // Determine which list the configuration belongs to
            const isProposed = proposedPackages.some((entry) => entry.id === configuration.id);
            const targetPackages = isProposed ? proposedPackages : normalPackages;
            // Find the package index within its group
            const packageIndex = targetPackages.findIndex((entry) => entry.id === configuration.id);
            if (packageIndex === -1) {
                return;
            }
            // Check if the move is possible within the group
            if (direction === 'up' && packageIndex === 0) {
                return;
            }
            if (direction === 'down' && packageIndex === targetPackages.length - 1) {
                return;
            }
            // Move the package within its group
            const tempPackage = targetPackages[packageIndex];
            targetPackages.splice(packageIndex, 1);
            if (tempPackage) {
                const newIndex = direction === 'up' ? packageIndex - 1 : packageIndex + 1;
                targetPackages.splice(newIndex, 0, tempPackage);
                // Update 'order' property for normal packages
                const updatedNormalPackages = (isProposed ? normalPackages : targetPackages).map((pkg, idx) => (Object.assign(Object.assign({}, pkg), { order: idx })));
                // Update 'order' property for proposed packages
                const updatedProposedPackages = (isProposed ? targetPackages : proposedPackages).map((pkg, idx) => (Object.assign(Object.assign({}, pkg), { order: idx })));
                // Recombine: normal first, then proposed
                const allPackages = [
                    ...updatedNormalPackages,
                    ...updatedProposedPackages
                ];
                tempDealItems[index] = Object.assign(Object.assign({}, tempDealItem), { packages: allPackages });
            }
        }
        setDeal(Object.assign(Object.assign({}, deal), { items: tempDealItems }));
    };
    /**
     * @function hideDealItemConfiguration
     * @since 1.0.0
     * @hidden
     */
    const hideDealItemConfiguration = (dealItem, configuration, type) => {
        var _a, _b;
        const dealItemIndex = deal.items.findIndex((entry) => entry.id == dealItem.id);
        if (dealItemIndex === -1) {
            return;
        }
        if (type === 'option') {
            const tempOption = (_a = deal.items[dealItemIndex].options) === null || _a === void 0 ? void 0 : _a.map((entry) => {
                if (entry.id === configuration.id) {
                    return Object.assign(Object.assign({}, entry), { hide: !entry.hide });
                }
                return entry;
            });
            const tempDealItems = [...deal.items];
            tempDealItems[dealItemIndex] = Object.assign(Object.assign({}, tempDealItems[dealItemIndex]), { options: tempOption });
            setDeal(Object.assign(Object.assign({}, deal), { items: tempDealItems }));
        }
        else {
            const tempPackage = (_b = deal.items[dealItemIndex].packages) === null || _b === void 0 ? void 0 : _b.map((entry) => {
                if (entry.id === configuration.id) {
                    return Object.assign(Object.assign({}, entry), { hide: !entry.hide });
                }
                return entry;
            });
            const tempDealItems = [...deal.items];
            tempDealItems[dealItemIndex] = Object.assign(Object.assign({}, tempDealItems[dealItemIndex]), { packages: tempPackage });
            setDeal(Object.assign(Object.assign({}, deal), { items: tempDealItems }));
        }
    };
    /**
     * @function onItemAlternative
     * @since 1.0.0
     * @hidden
     */
    const onItemAlternative = (dealItem) => __awaiter(this, void 0, void 0, function* () {
        const { item, variant, options, packages, quantity } = yield presentPicker(modal, {
            deal,
            label: t('Add To Quote'),
            optionReplacement,
            primaryGroup,
            itemsDisplay,
            skipContact: true
        });
        addDealItem(item, variant, options, packages, quantity, dealItem.id);
    });
    /**
     * @function onItemClone
     * @since 1.0.0
     * @hidden
     */
    const onItemClone = (dealItem) => {
        const newId = uuidv4();
        const tempDealItems = [...deal.items];
        tempDealItems.push(Object.assign(Object.assign({}, dealItem), { id: newId }));
        setDeal(Object.assign(Object.assign({}, deal), { items: tempDealItems }));
        // scroll to item
        const element = document.getElementById('#' + newId);
        if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
        }
    };
    /**
     * @function onItemDelete
     * @since 1.0.0
     * @hidden
     */
    const onItemDelete = (dealItem) => {
        const tempDealItems = deal.items.filter((entry) => entry.id !== dealItem.id);
        setDeal(Object.assign(Object.assign({}, deal), { items: tempDealItems }));
    };
    /**
     * @function onReplaceItems
     * @since 1.0.0
     * @hidden
     */
    const onReplaceItems = () => {
        setOpenReplaceItemsModal(true);
    };
    /**
     * Replaces the lines with a copy of another deal's lines. Nothing is saved
     * until the quote is saved.
     * @function replaceItems
     * @since 1.0.0
     * @hidden
     */
    const replaceItems = (sourceId) => __awaiter(this, void 0, void 0, function* () {
        var _20;
        setReplaceItemsProcessing(true);
        try {
            const result = yield apollo.query({
                query: DealQuery,
                variables: { input: { id: sourceId } },
                fetchPolicy: 'network-only'
            });
            if (!result.data.deal) {
                throw new Error(`Deal ${sourceId} not found`);
            }
            const source = initializeDealOrder(result.data.deal);
            const items = copyDealItems((_20 = source.items) !== null && _20 !== void 0 ? _20 : []);
            setDeal(Object.assign(Object.assign({}, deal), { items }));
            setOpenReplaceItemsModal(false);
            alert.present({
                title: (React.createElement("p", null, t('Content replaced. Save the quote to keep the changes.')))
            });
            if (getInactiveDealItems(items).length) {
                alert.present({
                    title: React.createElement("p", null, t('One or more items are inactive. Please check.')),
                    style: 'warning'
                });
            }
        }
        catch (error) {
            console.error(error);
            alert.present({
                title: React.createElement("p", null, t('Something went wrong. Try later.')),
                style: 'error'
            });
        }
        setReplaceItemsProcessing(false);
    });
    /* -- Packages -- */
    /**
     * @function updatePackages
     * @since 1.0.0
     */
    const updatePackages = (dealItem, alternativeTo) => __awaiter(this, void 0, void 0, function* () {
        const { item, variant, options, packages } = yield presentPicker(modal, {
            deal,
            item: dealItem.item,
            variant: dealItem.variant,
            options: getSelectedOptions(dealItem),
            packages: getSelectedPackages(dealItem),
            label: t('Update Quote'),
            step: 2,
            optionReplacement,
            isFinalStep: true,
            primaryGroup,
            itemsDisplay,
            quantity: dealItem.quantity,
            alternativeTo
        });
        updateDealItem(dealItem, item, variant, options, packages);
    });
    /**
     * @function onPackageQuantityChange
     * @since 1.0.0
     * @hidden
     */
    const onPackageQuantityChange = (dealItem, pack, quantity) => {
        function execute(makeChange = true) {
            setDeal((prevDeal) => prevDeal
                ? Object.assign(Object.assign({}, prevDeal), { items: prevDeal.items.map((item) => {
                        var _a, _b;
                        return item.id === dealItem.id
                            ? Object.assign(Object.assign({}, item), { packages: (_b = (_a = item.packages) === null || _a === void 0 ? void 0 : _a.map((p) => p.id === pack.id
                                    ? Object.assign(Object.assign({}, p), { quantity: makeChange ? quantity : p.quantity }) : p)) !== null && _b !== void 0 ? _b : [] }) : item;
                    }) }) : prevDeal);
        }
        if (quantity !== dealItem.quantity && !optionAndPackageQuantityAlertShown) {
            modal.present({
                style: 'dialog',
                title: t('Update quantity'),
                picto: 'edit',
                modal: (React.createElement(Confirm, { title: `${t('You are about to change the quantity that is different from the product quantity. Are you sure you want to continue?')}`, infos: t('Selecting "Yes" will update the quantity of the package. Selecting "No" will cancel the update.'), acceptText: t('Yes'), refuseText: t('No'), onAccept: () => {
                        execute(true);
                        setOptionAndPackageQuantityAlertShown(false);
                    }, onRefuse: () => {
                        execute(false);
                        setOptionAndPackageQuantityAlertShown(true);
                    } }))
            });
        }
        else {
            execute(true);
        }
    };
    /**
     * @function onPackageDelete
     * @since 1.0.0
     * @hidden
     */
    const onPackageDelete = (dealItem, configuration) => {
        var _a;
        const dealItemIndex = deal.items.findIndex((entry) => entry.id == dealItem.id);
        if (dealItemIndex === -1) {
            return;
        }
        const tempPackage = (_a = deal.items[dealItemIndex].packages) === null || _a === void 0 ? void 0 : _a.filter((entry) => entry.id !== configuration.id);
        const tempDealItems = [...deal.items];
        tempDealItems[dealItemIndex] = Object.assign(Object.assign({}, tempDealItems[dealItemIndex]), { packages: tempPackage });
        setDeal(Object.assign(Object.assign({}, deal), { items: tempDealItems }));
    };
    /**
     * @function proposePackage
     * @since 1.0.0
     */
    const proposePackage = (dealItem, configuration) => {
        var _a;
        const packages = [];
        if (dealItem.packages) {
            for (const pack of dealItem.packages) {
                if (pack.package == null) {
                    continue;
                }
                if (pack.package.id == ((_a = configuration.package) === null || _a === void 0 ? void 0 : _a.id)) {
                    packages.push(Object.assign(Object.assign({}, pack), { proposed: true }));
                }
                else {
                    packages.push(pack);
                }
            }
        }
        const tempDealItems = [...deal.items];
        const dealItemIndex = tempDealItems.findIndex((item) => item.id === dealItem.id);
        if (dealItemIndex > -1) {
            tempDealItems[dealItemIndex] = Object.assign(Object.assign({}, tempDealItems[dealItemIndex]), { packages });
            setDeal(Object.assign(Object.assign({}, deal), { items: tempDealItems }));
        }
    };
    /**
     * @function includePackage
     * @since 1.0.0
     * @hidden
     * */
    const includePackage = (dealItem, configuration) => {
        var _a, _b, _c, _d;
        const packages = [];
        const dealItemIndex = deal.items.findIndex((entry) => entry.id == dealItem.id);
        if (dealItem.packages) {
            for (const pack of dealItem.packages) {
                if (((_a = pack.package) === null || _a === void 0 ? void 0 : _a.id) == ((_b = configuration.package) === null || _b === void 0 ? void 0 : _b.id)) {
                    packages.push(Object.assign(Object.assign({}, pack), { proposed: false }));
                }
                else {
                    packages.push(pack);
                }
            }
        }
        // If it's an alternative, we need to make original package proposed
        if (configuration.alternativeTo) {
            const originalPackage = (_c = dealItem.packages) === null || _c === void 0 ? void 0 : _c.find((entry) => { var _a; return ((_a = entry.package) === null || _a === void 0 ? void 0 : _a.id) == configuration.alternativeTo; });
            if (originalPackage) {
                const index = packages.findIndex((entry) => { var _a; return ((_a = entry.package) === null || _a === void 0 ? void 0 : _a.id) == configuration.alternativeTo; });
                packages.splice(index, 1);
                packages.push(Object.assign(Object.assign({}, originalPackage), { proposed: true, alternativeTo: (_d = configuration.package) === null || _d === void 0 ? void 0 : _d.id }));
            }
        }
        const tempDealItems = [...deal.items];
        tempDealItems[dealItemIndex] = Object.assign(Object.assign({}, tempDealItems[dealItemIndex]), { packages });
        setDeal(Object.assign(Object.assign({}, deal), { items: tempDealItems }));
    };
    /* -- Options -- */
    /**
     * @function updateOptions
     * @since 1.0.0
     */
    const updateOptions = (dealItem, alternativeTo, initialRendering) => __awaiter(this, void 0, void 0, function* () {
        const { item, variant, options, packages } = yield presentPicker(modal, {
            deal,
            item: dealItem.item,
            variant: dealItem.variant,
            generateOptions: true,
            options: getSelectedOptions(dealItem),
            packages: getSelectedPackages(dealItem),
            label: t('Update Quote'),
            step: 3,
            optionReplacement,
            primaryGroup,
            itemsDisplay,
            isFinalStep: true,
            quantity: dealItem.quantity,
            alternativeTo,
            initialRendering
        });
        updateDealItem(dealItem, item, variant, options, packages);
    });
    /**
     * @function onOptionsDelete
     * @since 1.0.0
     * @hidden
     */
    const onOptionsDelete = (dealItem) => {
        const dealItemIndex = deal.items.findIndex((entry) => entry.id == dealItem.id);
        const tempDealItems = [...deal.items];
        tempDealItems[dealItemIndex] = Object.assign(Object.assign({}, tempDealItems[dealItemIndex]), { options: [] });
        setDeal(Object.assign(Object.assign({}, deal), { items: tempDealItems }));
    };
    /* -- Option -- */
    /**
     * @function onOptionQuantityChange
     * @since 1.0.0
     * @hidden
     */
    const onOptionQuantityChange = (dealItem, option, quantity) => {
        var _a, _b, _c, _d;
        function execute(makeChange = true) {
            setDeal((prevDeal) => prevDeal
                ? Object.assign(Object.assign({}, prevDeal), { items: prevDeal.items.map((item) => {
                        var _a, _b;
                        return item.id === dealItem.id
                            ? Object.assign(Object.assign({}, item), { options: (_b = (_a = item.options) === null || _a === void 0 ? void 0 : _a.map((opt) => opt.id === option.id
                                    ? Object.assign(Object.assign({}, opt), { quantity: makeChange ? quantity : opt.quantity }) : opt)) !== null && _b !== void 0 ? _b : [] }) : item;
                    }) }) : prevDeal);
        }
        function updateRelatedOptionsQuantity() {
            var _a, _b, _c, _d;
            const dealItemOption = (_a = dealItem.item.options) === null || _a === void 0 ? void 0 : _a.find((opt) => { var _a, _b; return ((_a = opt.item) === null || _a === void 0 ? void 0 : _a.id) == ((_b = option.item) === null || _b === void 0 ? void 0 : _b.id); });
            // Combine relations from both dealItemOption and its item, handling type differences
            const relatedOptions = [
                ...((_b = dealItemOption === null || dealItemOption === void 0 ? void 0 : dealItemOption.relations) !== null && _b !== void 0 ? _b : []),
                ...((_d = (_c = dealItemOption === null || dealItemOption === void 0 ? void 0 : dealItemOption.item) === null || _c === void 0 ? void 0 : _c.relations) !== null && _d !== void 0 ? _d : [])
            ];
            // Update related options quantity
            setDeal((prevDeal) => prevDeal
                ? Object.assign(Object.assign({}, prevDeal), { items: prevDeal.items.map((item) => {
                        var _a, _b;
                        return item.id === dealItem.id
                            ? Object.assign(Object.assign({}, item), { options: (_b = (_a = item.options) === null || _a === void 0 ? void 0 : _a.map((opt) => {
                                    var _a;
                                    let relatedOptionQuantity = 0;
                                    const isRelatedOption = relatedOptions === null || relatedOptions === void 0 ? void 0 : relatedOptions.some((rel) => { var _a, _b; return ((_a = rel.relatedSaleItem) === null || _a === void 0 ? void 0 : _a.id) == ((_b = opt.item) === null || _b === void 0 ? void 0 : _b.id); });
                                    if (isRelatedOption) {
                                        const relatedOption = relatedOptions === null || relatedOptions === void 0 ? void 0 : relatedOptions.find((rel) => { var _a, _b; return ((_a = rel.relatedSaleItem) === null || _a === void 0 ? void 0 : _a.id) == ((_b = opt.item) === null || _b === void 0 ? void 0 : _b.id); });
                                        if (relatedOption) {
                                            relatedOptionQuantity =
                                                relatedOption.quantity * quantity;
                                            // check in options if the related option is an relation of another option
                                            (_a = item.options) === null || _a === void 0 ? void 0 : _a.forEach((o) => {
                                                var _a, _b, _c, _d, _e, _f, _g;
                                                if (o.proposed) {
                                                    return;
                                                }
                                                const relatedOptionFromItem = (_a = item.item.options) === null || _a === void 0 ? void 0 : _a.find((opt) => {
                                                    var _a, _b, _c, _d;
                                                    return ((_a = opt.item) === null || _a === void 0 ? void 0 : _a.id) == ((_b = o.item) === null || _b === void 0 ? void 0 : _b.id) &&
                                                        ((_c = opt.item) === null || _c === void 0 ? void 0 : _c.id) !==
                                                            ((_d = relatedOption.saleItem) === null || _d === void 0 ? void 0 : _d.id);
                                                });
                                                if (relatedOptionFromItem) {
                                                    const relationOptionFromItemRelations = [
                                                        ...((_b = relatedOptionFromItem.relations) !== null && _b !== void 0 ? _b : []),
                                                        ...((_d = (_c = relatedOptionFromItem.item) === null || _c === void 0 ? void 0 : _c.relations) !== null && _d !== void 0 ? _d : [])
                                                    ];
                                                    // get relation
                                                    const r = relationOptionFromItemRelations === null || relationOptionFromItemRelations === void 0 ? void 0 : relationOptionFromItemRelations.find((r) => {
                                                        var _a, _b;
                                                        return ((_a = r.relatedSaleItem) === null || _a === void 0 ? void 0 : _a.id) ===
                                                            ((_b = relatedOption.relatedSaleItem) === null || _b === void 0 ? void 0 : _b.id) &&
                                                            r.type === 'CONDITIONAL';
                                                    });
                                                    if (r) {
                                                        // get quantity from dealItem.options
                                                        const relatedOptionFromItemQuantity = (_g = (_f = (_e = item.options) === null || _e === void 0 ? void 0 : _e.find((o) => {
                                                            var _a, _b, _c, _d;
                                                            return ((_a = o.item) === null || _a === void 0 ? void 0 : _a.id) ==
                                                                ((_b = relatedOptionFromItem.item) === null || _b === void 0 ? void 0 : _b.id) &&
                                                                ((_c = o.item) === null || _c === void 0 ? void 0 : _c.id) !==
                                                                    ((_d = r.relatedSaleItem) === null || _d === void 0 ? void 0 : _d.id);
                                                        })) === null || _f === void 0 ? void 0 : _f.quantity) !== null && _g !== void 0 ? _g : 1;
                                                        relatedOptionQuantity +=
                                                            r.quantity *
                                                                relatedOptionFromItemQuantity;
                                                    }
                                                }
                                            });
                                        }
                                        return Object.assign(Object.assign({}, opt), { quantity: Math.round(relatedOptionQuantity * 100) / 100 });
                                    }
                                    if (opt.id === option.id) {
                                        return Object.assign(Object.assign({}, opt), { quantity: quantity });
                                    }
                                    return opt;
                                })) !== null && _b !== void 0 ? _b : [] }) : item;
                    }) }) : prevDeal);
        }
        // Check if option has related options that have a quantity different from the option quantity
        const dealItemOption = (_a = dealItem.item.options) === null || _a === void 0 ? void 0 : _a.find((opt) => { var _a, _b; return ((_a = opt.item) === null || _a === void 0 ? void 0 : _a.id) == ((_b = option.item) === null || _b === void 0 ? void 0 : _b.id); });
        const hasQuantityDifferentFromOptionQuantity = ((_b = dealItemOption === null || dealItemOption === void 0 ? void 0 : dealItemOption.relations) === null || _b === void 0 ? void 0 : _b.some((rel) => rel.quantity !== quantity)) ||
            ((_d = (_c = dealItemOption === null || dealItemOption === void 0 ? void 0 : dealItemOption.item) === null || _c === void 0 ? void 0 : _c.relations) === null || _d === void 0 ? void 0 : _d.some((rel) => rel.quantity !== quantity));
        if (hasQuantityDifferentFromOptionQuantity) {
            modal.present({
                style: 'dialog',
                title: t('Update related options quantity'),
                picto: 'edit',
                modal: (React.createElement(Confirm, { title: t('Update related options quantity'), infos: t('You are about to change the quantity of an option that has a related option. This will update the quantity of the related option. Are you sure you want to continue?'), acceptText: t('Yes'), refuseText: t('No'), onAccept: () => {
                        updateRelatedOptionsQuantity();
                    }, onRefuse: () => {
                        execute(true);
                    } }))
            });
        }
        else if (quantity !== dealItem.quantity &&
            !optionAndPackageQuantityAlertShown) {
            modal.present({
                style: 'dialog',
                title: t('Update quantity'),
                picto: 'edit',
                modal: (React.createElement(Confirm, { title: `${t('You are about to change the quantity that is different from the product quantity. Are you sure you want to continue?')}`, infos: t('Selecting "Yes" will update the quantity of the option. Selecting "No" will cancel the update.'), acceptText: t('Yes'), refuseText: t('No'), onAccept: () => {
                        setOptionAndPackageQuantityAlertShown(true);
                        execute(true);
                    }, onRefuse: () => {
                        setOptionAndPackageQuantityAlertShown(true);
                        execute(false);
                    } }))
            });
        }
        else {
            execute(true);
        }
    };
    /**
     * @function proposeOption
     * @since 1.0.0
     */
    const proposeOption = (dealItem, configuration) => {
        setDeal((prevDeal) => {
            var _a, _b, _c;
            if (!prevDeal) {
                return prevDeal;
            }
            const tempDealItems = [...prevDeal.items];
            const dealItemIndex = tempDealItems.findIndex((item) => item.id === dealItem.id);
            if (dealItemIndex === -1) {
                return prevDeal;
            }
            const currentDealItem = tempDealItems[dealItemIndex];
            const currentOptions = currentDealItem.options;
            if (!(currentOptions === null || currentOptions === void 0 ? void 0 : currentOptions.length)) {
                return prevDeal;
            }
            const alternativeTo = currentOptions.find((entry) => { var _a; return entry.alternativeTo == ((_a = configuration.item) === null || _a === void 0 ? void 0 : _a.id); });
            const matchesConfiguration = (option) => {
                var _a, _b, _c;
                return (configuration.id && option.id === configuration.id) ||
                    (((_a = configuration.item) === null || _a === void 0 ? void 0 : _a.id) && ((_b = option.item) === null || _b === void 0 ? void 0 : _b.id) === ((_c = configuration.item) === null || _c === void 0 ? void 0 : _c.id));
            };
            let options = currentOptions.map((option) => {
                var _a, _b;
                if (matchesConfiguration(option)) {
                    return Object.assign(Object.assign({}, option), { proposed: true, alternativeTo: alternativeTo
                            ? ((_b = (_a = alternativeTo.item) === null || _a === void 0 ? void 0 : _a.id) !== null && _b !== void 0 ? _b : null)
                            : option.alternativeTo });
                }
                if (alternativeTo && option.id === alternativeTo.id) {
                    return Object.assign(Object.assign({}, option), { proposed: false, alternativeTo: null });
                }
                return option;
            });
            const optionNeedToBeReincluded = (_b = (_a = currentDealItem.item) === null || _a === void 0 ? void 0 : _a.options) === null || _b === void 0 ? void 0 : _b.find((option) => {
                var _a;
                return (_a = option.relations) === null || _a === void 0 ? void 0 : _a.find((relation) => {
                    var _a, _b;
                    return relation.type == 'INCOMPATIBLE' &&
                        ((_a = relation.relatedSaleItem) === null || _a === void 0 ? void 0 : _a.id) == ((_b = configuration.item) === null || _b === void 0 ? void 0 : _b.id);
                });
            });
            if (optionNeedToBeReincluded &&
                !options.some((opt) => { var _a, _b; return ((_a = opt.item) === null || _a === void 0 ? void 0 : _a.id) === ((_b = optionNeedToBeReincluded.item) === null || _b === void 0 ? void 0 : _b.id); })) {
                options = [
                    ...options,
                    Object.assign(Object.assign({}, optionNeedToBeReincluded), { id: uuidv4(), price: calculatePriceCurrency((_c = optionNeedToBeReincluded.item.price) !== null && _c !== void 0 ? _c : 0, prevDeal.currency), quantity: 1, type: 'OPTION', priceDisplay: 'AMOUNT', hide: false, proposed: false, createdAt: optionNeedToBeReincluded.item.createdAt, updatedAt: optionNeedToBeReincluded.item.updatedAt })
                ];
            }
            tempDealItems[dealItemIndex] = Object.assign(Object.assign({}, currentDealItem), { options });
            return Object.assign(Object.assign({}, prevDeal), { items: tempDealItems });
        });
    };
    /**
     * @function includeOption
     * @since 1.0.0
     */
    const includeOption = (dealItem, configuration, newOption = false) => __awaiter(this, void 0, void 0, function* () {
        var _21, _22, _23, _24, _25, _26;
        const options = [];
        const itemsToDelete = [];
        const tempDealItems = [...deal.items];
        const dealItemIndex = tempDealItems.findIndex((item) => item.id === dealItem.id);
        // Use options from the current deal state, not from the parameter
        const currentOptions = dealItemIndex > -1 && tempDealItems[dealItemIndex].options
            ? tempDealItems[dealItemIndex].options
            : dealItem.options;
        const linkedOptionIds = new Set();
        for (const source of [dealItem.item, dealItem.variant]) {
            for (const option of (_21 = source === null || source === void 0 ? void 0 : source.options) !== null && _21 !== void 0 ? _21 : []) {
                if ((_22 = option.item) === null || _22 === void 0 ? void 0 : _22.id) {
                    linkedOptionIds.add(option.item.id);
                }
            }
        }
        // Get relations for the option. Conditional targets that are not options
        // of this quote item are catalog relations and must not be added.
        const relations = getRelations(dealItem.item, dealItem.variant, configuration.item, dealItemIndex > -1
            ? tempDealItems[dealItemIndex].options
            : dealItem.options, true).filter((relation) => {
            var _a;
            if (relation.type !== 'CONDITIONAL') {
                return true;
            }
            const relatedId = (_a = relation.relatedSaleItem) === null || _a === void 0 ? void 0 : _a.id;
            return relatedId != null && linkedOptionIds.has(relatedId);
        });
        if (currentOptions) {
            for (const option of currentOptions) {
                const { item } = option;
                if (item == null) {
                    continue;
                }
                if (item.id == ((_23 = configuration.item) === null || _23 === void 0 ? void 0 : _23.id)) {
                    if (relations.length > 0 && configuration.item) {
                        const isAccepted = yield editConfiguration(configuration.item, relations);
                        if (isAccepted) {
                            options.push(Object.assign(Object.assign({}, option), { proposed: false }));
                            for (const relation of relations) {
                                if (relation.type == 'CONDITIONAL') {
                                    // Check if option already exists
                                    const existingOptionIndex = options.findIndex((option) => { var _a, _b; return ((_a = option.item) === null || _a === void 0 ? void 0 : _a.id) === ((_b = relation.relatedSaleItem) === null || _b === void 0 ? void 0 : _b.id); });
                                    if (existingOptionIndex !== -1) {
                                        // Option exists, add to quantity
                                        const existingOption = Object.assign({}, options[existingOptionIndex]);
                                        existingOption.quantity +=
                                            dealItem.quantity * relation.quantity;
                                        options[existingOptionIndex] = existingOption;
                                    }
                                    else {
                                        // Option doesn't exist, create new one
                                        options.push({
                                            id: '',
                                            item: relation.relatedSaleItem,
                                            proposed: false,
                                            quantity: dealItem.quantity * relation.quantity,
                                            price: relation.relatedSaleItem.price || 0,
                                            priceDisplay: configuration.priceDisplay,
                                            hide: configuration.hide,
                                            type: relation.type,
                                            createdAt: undefined,
                                            updatedAt: undefined
                                        });
                                    }
                                }
                                else if (relation.type == 'INCOMPATIBLE') {
                                    itemsToDelete.push(relation.relatedSaleItem);
                                }
                            }
                        }
                        else {
                            options.push(Object.assign(Object.assign({}, option), { proposed: true }));
                        }
                    }
                    else {
                        options.push(Object.assign(Object.assign({}, option), { proposed: false }));
                    }
                }
                else {
                    options.push(option);
                }
            }
        }
        // Add new option
        if (newOption) {
            if ((currentOptions === null || currentOptions === void 0 ? void 0 : currentOptions.find((entry) => { var _a, _b; return ((_a = entry.item) === null || _a === void 0 ? void 0 : _a.id) == ((_b = configuration.item) === null || _b === void 0 ? void 0 : _b.id); })) == null) {
                if (relations.length > 0 && configuration.item) {
                    const isAccepted = yield editConfiguration(configuration.item, relations);
                    if (isAccepted) {
                        options.push(configuration);
                        for (const relation of relations) {
                            if (relation.type == 'CONDITIONAL') {
                                const existingOptionIndex = options.findIndex((option) => { var _a, _b; return ((_a = option.item) === null || _a === void 0 ? void 0 : _a.id) === ((_b = relation.relatedSaleItem) === null || _b === void 0 ? void 0 : _b.id); });
                                if (existingOptionIndex !== -1) {
                                    const existingOption = Object.assign({}, options[existingOptionIndex]);
                                    existingOption.quantity +=
                                        dealItem.quantity * relation.quantity;
                                    options[existingOptionIndex] = existingOption;
                                }
                                else {
                                    options.push({
                                        id: '',
                                        item: relation.relatedSaleItem,
                                        proposed: false,
                                        quantity: dealItem.quantity * relation.quantity,
                                        price: relation.relatedSaleItem.price || 0,
                                        priceDisplay: configuration.priceDisplay,
                                        hide: configuration.hide,
                                        type: relation.type,
                                        createdAt: undefined,
                                        updatedAt: undefined
                                    });
                                }
                            }
                            else if (relation.type == 'INCOMPATIBLE') {
                                itemsToDelete.push(relation.relatedSaleItem);
                            }
                        }
                    }
                }
                else {
                    options.push(configuration);
                }
            }
        }
        // Check if we have a unique restriction for group
        if ((_25 = (_24 = configuration.item) === null || _24 === void 0 ? void 0 : _24.group) === null || _25 === void 0 ? void 0 : _25.hasUniqueOptionRestriction) {
            const optionAlreadySelected = options.find((entry) => {
                var _a, _b, _c, _d, _e, _f;
                return ((_b = (_a = entry.item) === null || _a === void 0 ? void 0 : _a.group) === null || _b === void 0 ? void 0 : _b.id) == ((_d = (_c = configuration.item) === null || _c === void 0 ? void 0 : _c.group) === null || _d === void 0 ? void 0 : _d.id) &&
                    ((_e = entry.item) === null || _e === void 0 ? void 0 : _e.id) != ((_f = configuration.item) === null || _f === void 0 ? void 0 : _f.id);
            });
            if (optionAlreadySelected === null || optionAlreadySelected === void 0 ? void 0 : optionAlreadySelected.item) {
                itemsToDelete.push(optionAlreadySelected.item);
            }
        }
        // Delete incompatible items
        if (itemsToDelete.length > 0) {
            for (const item of itemsToDelete) {
                const index = options.findIndex((entry) => { var _a; return ((_a = entry.item) === null || _a === void 0 ? void 0 : _a.id) == item.id; });
                if (index > -1) {
                    options.splice(index, 1);
                }
            }
        }
        // If it's an alternative, we need to make original option proposed
        if (configuration.alternativeTo) {
            const originalOption = currentOptions === null || currentOptions === void 0 ? void 0 : currentOptions.find((entry) => { var _a; return ((_a = entry.item) === null || _a === void 0 ? void 0 : _a.id) == configuration.alternativeTo; });
            if (originalOption) {
                const index = options.findIndex((entry) => { var _a; return ((_a = entry.item) === null || _a === void 0 ? void 0 : _a.id) == configuration.alternativeTo; });
                options.splice(index, 1);
                options.push(Object.assign(Object.assign({}, originalOption), { proposed: true, alternativeTo: (_26 = configuration.item) === null || _26 === void 0 ? void 0 : _26.id }));
            }
        }
        if (dealItemIndex > -1) {
            tempDealItems[dealItemIndex] = Object.assign(Object.assign({}, tempDealItems[dealItemIndex]), { options });
            setDeal(Object.assign(Object.assign({}, deal), { items: tempDealItems }));
        }
    });
    /**
     * @function changePriceDisplay
     * @since 1.0.0
     * @hidden
     */
    const changePriceDisplay = (dealItem, configuration, withPrice, type, display) => {
        var _a, _b, _c, _d, _e, _f, _g, _h;
        if (type === 'option') {
            const options = [];
            const dealItemIndex = deal.items.findIndex((entry) => entry.id == dealItem.id);
            if (deal.items[dealItemIndex] && deal.items[dealItemIndex].options) {
                for (const option of deal.items[dealItemIndex].options) {
                    const { item } = option;
                    if (item == null) {
                        continue;
                    }
                    if (item.id == ((_a = configuration.item) === null || _a === void 0 ? void 0 : _a.id)) {
                        options.push(Object.assign(Object.assign({}, option), { price: withPrice
                                ? display === 'DEFAULT'
                                    ? ((_c = (_b = option.item) === null || _b === void 0 ? void 0 : _b.price) !== null && _c !== void 0 ? _c : 0)
                                    : ((_d = option.price) !== null && _d !== void 0 ? _d : 0)
                                : 0, included: display === 'DEFAULT' ? false : true, priceDisplay: display === 'DEFAULT' ? 'AMOUNT' : display }));
                    }
                    else {
                        options.push(option);
                    }
                }
            }
            const tempDealItems = [...deal.items];
            tempDealItems[dealItemIndex] = Object.assign(Object.assign({}, tempDealItems[dealItemIndex]), { options });
            setDeal(Object.assign(Object.assign({}, deal), { items: tempDealItems }));
        }
        else {
            const packages = [];
            const dealItemIndex = deal.items.findIndex((entry) => entry.id == dealItem.id);
            if (deal.items[dealItemIndex] && deal.items[dealItemIndex].packages) {
                for (const pack of deal.items[dealItemIndex].packages) {
                    const { package: item } = pack;
                    if (item == null) {
                        continue;
                    }
                    if (item.id == ((_e = configuration.package) === null || _e === void 0 ? void 0 : _e.id)) {
                        packages.push(Object.assign(Object.assign({}, pack), { price: withPrice
                                ? display === 'DEFAULT'
                                    ? ((_g = (_f = pack.package) === null || _f === void 0 ? void 0 : _f.price) !== null && _g !== void 0 ? _g : 0)
                                    : ((_h = pack.price) !== null && _h !== void 0 ? _h : 0)
                                : 0, included: display === 'DEFAULT' ? false : true, priceDisplay: display === 'DEFAULT' ? 'AMOUNT' : display }));
                    }
                    else {
                        packages.push(pack);
                    }
                }
            }
            const tempDealItems = [...deal.items];
            tempDealItems[dealItemIndex] = Object.assign(Object.assign({}, tempDealItems[dealItemIndex]), { packages });
            setDeal(Object.assign(Object.assign({}, deal), { items: tempDealItems }));
        }
    };
    /**
     * @function onOptionDelete
     * @since 1.0.0
     * @hidden
     */
    const onOptionDelete = (dealItem, option, relatedOptions) => {
        var _a, _b, _c, _d, _e;
        const dealItemIndex = deal.items.findIndex((entry) => entry.id == dealItem.id);
        if (dealItemIndex === -1) {
            return;
        }
        const optionQuantity = option.quantity;
        // Get relations for the option being deleted
        const dealItemOption = (_a = dealItem.item.options) === null || _a === void 0 ? void 0 : _a.find((opt) => { var _a, _b; return ((_a = opt.item) === null || _a === void 0 ? void 0 : _a.id) == ((_b = option.item) === null || _b === void 0 ? void 0 : _b.id); });
        const relatedOptionsFromOption = [
            ...((_b = dealItemOption === null || dealItemOption === void 0 ? void 0 : dealItemOption.relations) !== null && _b !== void 0 ? _b : []),
            ...((_d = (_c = option === null || option === void 0 ? void 0 : option.item) === null || _c === void 0 ? void 0 : _c.relations) !== null && _d !== void 0 ? _d : [])
        ];
        // Filter out the option being deleted
        const tempOptions = (_e = deal.items[dealItemIndex].options) === null || _e === void 0 ? void 0 : _e.filter((entry) => entry.id !== option.id);
        // Update quantities for remaining related options and remove those with 0 quantity
        const updatedOptions = tempOptions === null || tempOptions === void 0 ? void 0 : tempOptions.reduce((acc, opt) => {
            // Check if this option is related to the deleted option
            const relatedRelation = relatedOptionsFromOption.find((relation) => {
                var _a, _b;
                return ((_a = relation.relatedSaleItem) === null || _a === void 0 ? void 0 : _a.id) === ((_b = opt.item) === null || _b === void 0 ? void 0 : _b.id) &&
                    relation.type === 'CONDITIONAL';
            });
            if (relatedRelation) {
                // Calculate the quantity to reduce
                const quantityToReduce = optionQuantity * relatedRelation.quantity;
                const newQuantity = Math.max(0, opt.quantity - quantityToReduce);
                // If quantity becomes 0, don't add to the array
                if (newQuantity > 0) {
                    acc.push(Object.assign(Object.assign({}, opt), { quantity: Math.round(newQuantity * 100) / 100 }));
                }
            }
            else {
                acc.push(opt);
            }
            return acc;
        }, []);
        const tempDealItems = [...deal.items];
        tempDealItems[dealItemIndex] = Object.assign(Object.assign({}, tempDealItems[dealItemIndex]), { options: updatedOptions });
        setDeal(Object.assign(Object.assign({}, deal), { items: tempDealItems }));
    };
    /**
     * @function editConfiguration
     * @since 1.0.0
     * @return boolean
     * @hidden
     **/
    function editConfiguration(item, relations) {
        return new Promise((resolve, reject) => {
            const onAccept = () => {
                modal.dismiss();
                resolve(true);
            };
            const onDiscard = () => {
                modal.dismiss();
                resolve(false);
            };
            modal.present({
                style: 'dialog',
                title: t('Edit Configuration'),
                picto: 'check',
                modal: (React.createElement(DealItemPickerOptionAlert, { item: item, relations: relations, onAccept: onAccept, onDiscard: onDiscard }))
            });
        });
    }
    /**
     * @function updateDealField
     * @since 1.0.0
     * @hidden
     */
    const updateDealField = (fieldKey, value, type) => {
        const castedValue = type == 'number' ? Number(value) : value;
        setDeal((prevState) => {
            var _a;
            if (prevState == null) {
                return undefined;
            }
            if (fieldKey === 'taxAmount' &&
                Number(prevState.taxAmount) === Number(castedValue)) {
                return prevState;
            }
            if (fieldKey === 'plannedDate') {
                const plannedDateMode = getConfigurationFieldValue((_a = configurationFields === null || configurationFields === void 0 ? void 0 : configurationFields.configurationFields) !== null && _a !== void 0 ? _a : [], 'PLANNED_DATE_MODE') || 'DATE';
                const { plannedDateStart, plannedDateEnd } = parsePlannedDateToRange(castedValue, plannedDateMode);
                return Object.assign(Object.assign({}, prevState), { plannedDate: castedValue || '', plannedDateStart,
                    plannedDateEnd });
            }
            return Object.assign(Object.assign({}, prevState), { [fieldKey]: castedValue });
        });
    };
    /**
     * @function updateDealFieldValues
     * @since 1.0.0
     * @hidden
     * */
    const updateDealFieldValues = (value, fieldId) => {
        setDeal((prevState) => {
            var _a;
            if (prevState == null) {
                return undefined;
            }
            return Object.assign(Object.assign({}, prevState), { fieldValues: addOrUpdateFieldValue((_a = prevState.fieldValues) !== null && _a !== void 0 ? _a : [], fieldId, value) });
        });
    };
    /**
     * @function updateDealItemFieldValues
     * @since 1.0.0
     * @hidden
     */
    const updateDealItemFieldValues = (dealItemId, value) => {
        setDeal((prevState) => {
            if (prevState == null) {
                return undefined;
            }
            return Object.assign(Object.assign({}, prevState), { items: prevState === null || prevState === void 0 ? void 0 : prevState.items.map((item) => item.id === dealItemId
                    ? Object.assign(Object.assign({}, item), { fieldValues: value }) : item) });
        });
    };
    /**
     * @function updateOptionFieldValues
     * @since 1.0.0
     * @hidden
     */
    const updateOptionFieldValues = (dealItemId, dealItemOptionId, value, applyToAll, startTime, endTime) => {
        setDeal((prevState) => {
            if (prevState == null) {
                return undefined;
            }
            return Object.assign(Object.assign({}, prevState), { items: prevState === null || prevState === void 0 ? void 0 : prevState.items.map((item) => {
                    var _a;
                    return item.id === dealItemId
                        ? Object.assign(Object.assign({}, item), { options: (_a = item.options) === null || _a === void 0 ? void 0 : _a.map((option) => option.id === dealItemOptionId || applyToAll
                                ? Object.assign(Object.assign(Object.assign(Object.assign({}, option), { fieldValues: value }), (startTime !== undefined ? { startTime } : {})), (endTime !== undefined ? { endTime } : {})) : option) }) : item;
                }) });
        });
    };
    /**
     * @function updatePackageFieldValues
     * @since 1.0.0
     * @hidden
     */
    const updatePackageFieldValues = (dealItemId, dealItemPackageId, value, applyToAll, startTime, endTime) => {
        setDeal((prevState) => {
            if (prevState == null) {
                return undefined;
            }
            return Object.assign(Object.assign({}, prevState), { items: prevState === null || prevState === void 0 ? void 0 : prevState.items.map((item) => {
                    var _a;
                    return item.id === dealItemId
                        ? Object.assign(Object.assign({}, item), { packages: (_a = item.packages) === null || _a === void 0 ? void 0 : _a.map((pack) => pack.id === dealItemPackageId || applyToAll
                                ? Object.assign(Object.assign(Object.assign(Object.assign({}, pack), { fieldValues: value }), (startTime !== undefined ? { startTime } : {})), (endTime !== undefined ? { endTime } : {})) : pack) }) : item;
                }) });
        });
    };
    /**
     * @function addOrUpdateFieldValue
     * @since 1.0.0
     * @hidden
     */
    const addOrUpdateFieldValue = (fieldValues, fieldId, value) => {
        const exist = !!fieldValues.find((fv) => fv.field.id === fieldId);
        const formattedValue = JSON.stringify({ value: value[fieldId] });
        if (exist) {
            return fieldValues.map((fv) => fv.field.id === fieldId ? Object.assign(Object.assign({}, fv), { value: formattedValue }) : fv);
        }
        else {
            return [
                ...fieldValues,
                { id: fieldId, value: formattedValue, field: { id: fieldId } }
            ];
        }
    };
    /**
     * @function updateOtherFees
     * @since 1.0.0
     * @hidden
     */
    const updateOtherFees = (otherFees) => {
        setDeal((prevDeal) => prevDeal ? Object.assign(Object.assign({}, prevDeal), { otherFees }) : prevDeal);
    };
    /**
     * @function updatePayments
     * @since 1.0.0
     * @hidden
     */
    const updatePayments = (payments) => {
        setDeal((prevDeal) => prevDeal
            ? Object.assign(Object.assign({}, prevDeal), { payments: applyPercentPaymentAmounts(payments, dealTotal) }) : prevDeal);
    };
    /**
     * @function updateMarkupPerItem
     * @since 1.0.0
     * @hidden
     */
    const updateMarkupPerItem = (id, value, applyToAll) => {
        var _a, _b;
        const tempMarkupPerItem = [...markupPerItem];
        const index = tempMarkupPerItem.findIndex((item) => item.id === id);
        if (index > -1) {
            tempMarkupPerItem[index] = { id, value };
        }
        else {
            tempMarkupPerItem.push({ id, value });
        }
        if (applyToAll) {
            const tempDealItems = [...deal.items];
            for (const item of tempDealItems) {
                if (item.id === id) {
                    item.packages = (_a = item.packages) === null || _a === void 0 ? void 0 : _a.map((pack) => {
                        const packPrice = pack.package ? pack.package.price : pack.price;
                        const pricePerUnitValue = getPricePerUnitFromMarkup(packPrice - (packPrice * discountRep) / 100, value);
                        return Object.assign(Object.assign({}, pack), { price: pricePerUnitValue });
                    });
                    item.options = (_b = item.options) === null || _b === void 0 ? void 0 : _b.map((option) => {
                        var _a, _b;
                        const optionPrice = getPriceFromItem(item.item, (_a = item.variant) !== null && _a !== void 0 ? _a : null, (_b = option.item) !== null && _b !== void 0 ? _b : null, 'option', deal.currency);
                        const pricePerUnitValue = getPricePerUnitFromMarkup(optionPrice ? optionPrice - (optionPrice * discountRep) / 100 : 0, value);
                        return Object.assign(Object.assign({}, option), { price: pricePerUnitValue });
                    });
                }
            }
            setDeal(Object.assign(Object.assign({}, deal), { items: tempDealItems }));
        }
        setMarkupPerItem(tempMarkupPerItem);
    };
    const context = {
        deal,
        dealTotal,
        globalMarkup,
        discountRep,
        pricingParameters,
        pricingParametersDisplay,
        itemHeaderType,
        configureItem,
        browsePicker,
        onItemPriceChange,
        updateItem,
        onItemAlternative,
        onItemQuantityChange,
        onItemDateChange,
        onItemClone,
        onItemDelete,
        onReplaceItems,
        onMoveItem,
        onMoveItemConfiguration,
        hideDealItemConfiguration,
        updatePackages,
        onPackageDelete,
        updateOptions,
        onOptionsDelete,
        includeOption,
        changePriceDisplay,
        proposeOption,
        proposePackage,
        includePackage,
        onOptionDelete,
        setDeal,
        setDealFieldsValues,
        setSubtotal,
        setTotal,
        updateDealField,
        updateOtherFees,
        updatePayments,
        configurationFields: (_s = configurationFields === null || configurationFields === void 0 ? void 0 : configurationFields.configurationFields) !== null && _s !== void 0 ? _s : [],
        hasDealItemConfigurationTime,
        quantityType,
        onOptionQuantityChange,
        onPackageQuantityChange,
        updateDealFieldValues,
        updateDealItemFieldValues,
        updateOptionFieldValues,
        updatePackageFieldValues,
        dealItemFields,
        setDealItemFields,
        dealItemConfigurationFields,
        setDealItemConfigurationFields,
        markupPerItem,
        updateMarkupPerItem,
        updateCurrency
    };
    return (React.createElement(DealConfiguratorContext.Provider, { value: context },
        React.createElement(Screen, { className: "deal-configure-screen", ready: ready, error: error },
            React.createElement(Header, null,
                React.createElement(DealStickyMenu, { onClickCard: (value) => onClickCard(value), selectedButton: selectedButton, openCard: openCard, onClose: onCloseCard })),
            React.createElement(Content, null, deal && (React.createElement(DealConfigurator, { deal: deal, setDealFieldsValues: setDealFieldsValues }))),
            React.createElement("div", { className: "deal-configure-screen-footer" }, !isPublish && deal.orderIn === false ? (React.createElement(React.Fragment, null,
                React.createElement("div", { className: "deal-configure-screen-footer-publish-container" },
                    React.createElement(Text, { value: t('Ready to send your interactive quote?'), className: "deal-configure-screen-footer-title" }),
                    deal.contact && ((_u = (_t = deal === null || deal === void 0 ? void 0 : deal.contact) === null || _t === void 0 ? void 0 : _t.primaryEmail) === null || _u === void 0 ? void 0 : _u.email) && (React.createElement(Text, { value: `${t('Your quote will be e-mailed to ')} ${(_v = deal === null || deal === void 0 ? void 0 : deal.contact) === null || _v === void 0 ? void 0 : _v.fullname} ${t('at')} ${(_x = (_w = deal === null || deal === void 0 ? void 0 : deal.contact) === null || _w === void 0 ? void 0 : _w.primaryEmail) === null || _x === void 0 ? void 0 : _x.email}.`, className: "deal-configure-screen-footer-infos" }))),
                React.createElement("div", null,
                    React.createElement(Button, { picto: "share-square", label: t('Send Quote'), onPress: onPressOpenModalPublish, disabled: !deal.contact })))) : (deal === null || deal === void 0 ? void 0 : deal.orderIn) === true ? (React.createElement("div", { className: "deal-configure-screen-footer-published-container" },
                React.createElement(Text, { value: t('This quote has been ordered.') }))) : (React.createElement("div", { className: "deal-configure-screen-footer-published-container" },
                React.createElement(Text, { value: t('This quote has been published.') }),
                React.createElement(Text, { value: `${t('To : ')} ${((_y = deal === null || deal === void 0 ? void 0 : deal.contact) === null || _y === void 0 ? void 0 : _y.fullname) ? (_z = deal === null || deal === void 0 ? void 0 : deal.contact) === null || _z === void 0 ? void 0 : _z.fullname : ''}` }),
                React.createElement("div", { className: "deal-configure-screen-footer-published-infos" },
                    React.createElement(Text, { value: `${t('By : ')} ${(deal === null || deal === void 0 ? void 0 : deal.user) ? deal === null || deal === void 0 ? void 0 : deal.user.fullname : ''}` }),
                    React.createElement(Text, { value: deal === null || deal === void 0 ? void 0 : deal.updatedAt }))))),
            openModalPublish && (React.createElement(DealConfiguratorModalPublish, { onClose: onCloseModalPublish, onPublish: onPublish, deal: deal, saveQuoteProcessing: saveQuoteProcessing, setSendMessage: setSendMessage })),
            openReplaceItemsModal && (React.createElement(DealReplaceItemsModal, { deal: deal, onClose: () => setOpenReplaceItemsModal(false), onConfirm: replaceItems, processing: replaceItemsProcessing })),
            openModalApprove && (React.createElement(DealApproveModal, { onClose: onCloseApprovalModal, onConfirm: onConfirmApprove })),
            openMewsBlockModal && pendingMewsSaveArgs && hasMewsConnector && (React.createElement(DealMewsAvailabilityBlockModal, { dealId: (_0 = deal === null || deal === void 0 ? void 0 : deal.id) !== null && _0 !== void 0 ? _0 : null, dealItems: (_1 = deal === null || deal === void 0 ? void 0 : deal.items) !== null && _1 !== void 0 ? _1 : [], mewsAssetId: (_3 = (_2 = mewsAccountAsset === null || mewsAccountAsset === void 0 ? void 0 : mewsAccountAsset.asset) === null || _2 === void 0 ? void 0 : _2.id) !== null && _3 !== void 0 ? _3 : null, onClose: () => {
                    setOpenMewsBlockModal(false);
                    saveDeal(pendingMewsSaveArgs.draft, pendingMewsSaveArgs.message, pendingMewsSaveArgs.sendNotif, true, false);
                }, onConfirm: (rateId) => {
                    setOpenMewsBlockModal(false);
                    saveDeal(pendingMewsSaveArgs.draft, pendingMewsSaveArgs.message, pendingMewsSaveArgs.sendNotif, true, true, rateId);
                } })),
            React.createElement(Sidebar, null, deal && (React.createElement(DealSidebar, { deal: deal, onApprove: onOpenApprovalModal, onSaveDraft: onSaveDraft, onPublish: onPressOpenModalPublish, isPublish: isPublish, saveDraftProcessing: saveDraftProcessing, setDealUser: setDealUser, setDealPartner: setDealPartner, setDealPartnerContact: setDealPartnerContact, setLanguage: setLanguage, dealUser: dealUser }))))));
}
/**
 * @component DealConfiguratorContext
 * @since 1.0.0
 */
export const DealConfiguratorContext = createContext({
    deal: null,
    globalMarkup: 0,
    dealTotal: 0,
    discountRep: 0,
    pricingParameters: 'DISCOUNT',
    pricingParametersDisplay: '',
    itemHeaderType: '',
    configureItem() { },
    browsePicker() { },
    changePriceDisplay() { },
    onItemPriceChange() { },
    updateItem() { },
    onItemQuantityChange() { },
    onItemDateChange() { },
    onItemAlternative() { },
    onItemClone() { },
    onItemDelete() { },
    onReplaceItems() { },
    onMoveItem() { },
    onMoveItemConfiguration() { },
    hideDealItemConfiguration() { },
    updatePackages() { },
    onPackageDelete() { },
    updateOptions() { },
    onOptionsDelete() { },
    proposeOption() { },
    includeOption() { },
    proposePackage() { },
    includePackage() { },
    onOptionDelete() { },
    setDeal() { },
    setDealFieldsValues() { },
    setSubtotal() { },
    setTotal() { },
    updateDealField() { },
    updateOtherFees() { },
    updatePayments() { },
    configurationFields: [],
    dealItemFields: [],
    setDealItemFields() { },
    dealItemConfigurationFields: [],
    setDealItemConfigurationFields() { },
    markupPerItem: [],
    updateMarkupPerItem() { },
    quantityType: '',
    updateCurrency() { },
    onOptionQuantityChange() { },
    onPackageQuantityChange() { },
    updateDealFieldValues() { },
    updateDealItemFieldValues() { },
    hasDealItemConfigurationTime: false,
    updateOptionFieldValues() { },
    updatePackageFieldValues() { }
});
/**
 * @function useDealConfiguratorContext
 * @since 1.0.0
 */
export function useDealConfiguratorContext() {
    return useContext(DealConfiguratorContext);
}
/**
 * @function presentPicker
 * @param source
 * @hidden
 */
const presentPicker = (modal, opts) => {
    const inventorySelection = false;
    const optionConfiguration = ['ITEM_ONLY'];
    const promise = new Promise((resolve) => {
        const { step, deal, item, variant, options, packages, label, generateOptions, optionReplacement, primaryGroup, isFinalStep, itemsDisplay, quantity, skipContact, alternativeTo, initialRendering } = opts;
        modal.present({
            picto: '',
            className: 'deal-configurator-modal',
            modal: (React.createElement(DealItemPicker, { step: step, deal: deal, item: item, label: label, variant: variant, options: options, packages: packages, inventorySelection: inventorySelection, optionConfiguration: optionConfiguration, generateOptions: generateOptions, optionReplacement: optionReplacement, primaryGroup: primaryGroup, isFinalStep: isFinalStep, itemsDisplay: itemsDisplay, quantity: quantity, skipContact: skipContact, alternativeTo: alternativeTo, initialRendering: initialRendering, onPick: (item, variant, packages, options, quantity) => {
                    resolve({ item, variant, options, packages, quantity });
                } }))
        });
    });
    return promise;
};
/**
 * @function getSelectedOptions
 * @since 1.0.0
 * @hidden
 */
const getSelectedOptions = (item) => {
    var _a, _b;
    return ((_b = (_a = item.options) === null || _a === void 0 ? void 0 : _a.filter((configuration) => configuration.item)) !== null && _b !== void 0 ? _b : []);
};
/**
 * @function getSelectedPackages
 * @since 1.0.0
 * @hidden
 */
const getSelectedPackages = (item) => {
    var _a, _b;
    return ((_b = (_a = item.packages) === null || _a === void 0 ? void 0 : _a.filter((configuration) => configuration.package)) !== null && _b !== void 0 ? _b : []);
};
// --------------------------------------------------------------------
/**
 * @const DealConfigureScreenInfos
 * @since 1.0.0
 */
export const DealConfigureScreenInfos = {
    title: t('Quote Configuration'),
    infos: t('Quote Configuration'),
    route: 'deal.configure'
};
/**
 * @function toUpdateDealItemInput
 * @param source
 * @hidden
 */
const toUpdateDealItemInput = (source) => {
    return source.map((item, index) => {
        var _a, _b, _c, _d, _e;
        return ({
            id: item.id,
            item: item.item.id,
            variant: (_a = item.variant) === null || _a === void 0 ? void 0 : _a.id,
            price: item.price,
            quantity: item.quantity,
            proposed: item.proposed || item.alternativeTo ? true : false,
            initiallyProposed: (_b = item.proposed) !== null && _b !== void 0 ? _b : false,
            alternativeTo: (_c = item.alternativeTo) !== null && _c !== void 0 ? _c : null,
            order: index,
            date: (_d = item.date) !== null && _d !== void 0 ? _d : null,
            packages: toDealItemInputPackages(item.packages),
            options: toDealItemInputOptions(item.options),
            fieldValues: (_e = item.fieldValues) === null || _e === void 0 ? void 0 : _e.map((fv) => ({
                value: fv.value,
                fieldId: fv.field ? fv.field.id : fv.id
            }))
        });
    });
};
/**
 * @function toDealItemInputPackages
 * @param source
 * @hidden
 */
const toDealItemInputPackages = (source) => {
    if (source == null) {
        return [];
    }
    const result = source.map((entry, index) => {
        var _a, _b, _c, _d, _e, _f, _g, _h;
        return ({
            package: (_a = entry.package) === null || _a === void 0 ? void 0 : _a.id,
            price: entry.price,
            priceDisplay: entry.priceDisplay,
            hide: entry.hide,
            order: index,
            quantity: entry.quantity,
            proposed: (_b = entry.proposed) !== null && _b !== void 0 ? _b : false,
            initiallyProposed: (_c = entry.proposed) !== null && _c !== void 0 ? _c : false,
            alternativeTo: (_d = entry.alternativeTo) !== null && _d !== void 0 ? _d : null,
            startTime: (_e = entry.startTime) !== null && _e !== void 0 ? _e : null,
            endTime: (_f = entry.endTime) !== null && _f !== void 0 ? _f : null,
            relatedItems: (_g = entry.relatedItems) === null || _g === void 0 ? void 0 : _g.map((entry) => {
                var _a;
                return ({
                    item: (_a = entry.item) === null || _a === void 0 ? void 0 : _a.id,
                    price: entry.price,
                    priceDisplay: entry.priceDisplay,
                    quantity: entry.quantity,
                    proposed: false
                });
            }),
            fieldValues: (_h = entry.fieldValues) === null || _h === void 0 ? void 0 : _h.map((fv) => ({
                value: fv.value,
                fieldId: fv.field ? fv.field.id : fv.id
            }))
        });
    });
    return result;
};
/**
 * @function toDealItemInputOptions
 * @param source
 * @hidden
 */
const toDealItemInputOptions = (source) => {
    if (source == null) {
        return [];
    }
    const result = source.map((entry, index) => {
        var _a, _b, _c, _d, _e, _f, _g, _h;
        return ({
            item: (_a = entry.item) === null || _a === void 0 ? void 0 : _a.id,
            price: entry.price,
            priceDisplay: entry.priceDisplay,
            hide: entry.hide,
            order: index,
            quantity: entry.quantity,
            included: entry.included,
            proposed: entry.proposed,
            initiallyProposed: (_b = entry.proposed) !== null && _b !== void 0 ? _b : false,
            alternativeTo: (_c = entry.alternativeTo) !== null && _c !== void 0 ? _c : null,
            priceList: (_d = entry.priceList) === null || _d === void 0 ? void 0 : _d.id,
            startTime: (_e = entry.startTime) !== null && _e !== void 0 ? _e : null,
            endTime: (_f = entry.endTime) !== null && _f !== void 0 ? _f : null,
            relatedItems: (_g = entry.relatedItems) === null || _g === void 0 ? void 0 : _g.map((entry) => {
                var _a;
                return ({
                    item: (_a = entry.item) === null || _a === void 0 ? void 0 : _a.id,
                    price: entry.price,
                    priceDisplay: entry.priceDisplay,
                    quantity: entry.quantity,
                    included: entry.included,
                    proposed: entry.proposed
                });
            }),
            fieldValues: (_h = entry.fieldValues) === null || _h === void 0 ? void 0 : _h.map((fv) => ({
                value: fv.value,
                fieldId: fv.field ? fv.field.id : fv.id
            }))
        });
    });
    return result;
};
